//src/services/api.js

import axios from 'axios';

// Auto-detect environment
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
export const API_BASE_URL = isLocalhost ? 'http://localhost:8000/api' : 'https://api.dev.schoolsync.ac/api';
export const BASE_URL = isLocalhost ? 'http://localhost:8000' : 'https://api.dev.schoolsync.ac'

const frontendEnv = (() => {
  if (isLocalhost) return 'local';

  const host = window.location.hostname.toLowerCase();
  if (host.includes('staging') || host.includes('dev')) {
    return 'staging';
  }

  return 'production';
})();

const ENVIRONMENT_SERVER_MESSAGES = {
  local: 'Server error. Check backend logs for details.',
  staging: 'Server error in this environment. Please contact support if it continues.',
  production: 'Something went wrong. Please try again later.',
};

const SENSITIVE_BACKEND_ERROR_PATTERNS = [
  /sqlstate/i,
  /pdoexception/i,
  /unknown database/i,
  /stack trace/i,
  /\sin\s.+\.php\s+on line\s+\d+/i,
  /syntax error or access violation/i,
];

const isSensitiveBackendMessage = (message) =>
  typeof message === 'string' &&
  SENSITIVE_BACKEND_ERROR_PATTERNS.some((pattern) => pattern.test(message));

const sanitizeErrorResponse = (error) => {
  if (!error?.response?.data || typeof error.response.data !== 'object') {
    return error;
  }

  const status = Number(error.response.status || 0);
  const data = error.response.data;
  const message = data.message;
  const shouldMaskMessage = status >= 500 || isSensitiveBackendMessage(message);

  if (shouldMaskMessage) {
    data.message = ENVIRONMENT_SERVER_MESSAGES[frontendEnv] || ENVIRONMENT_SERVER_MESSAGES.production;
    delete data.error;
  }

  if (frontendEnv !== 'local') {
    delete data.debug;
  }

  return error;
};
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add token and tenant header to requests
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenant = sessionStorage.getItem('tenant_id') || localStorage.getItem('tenant_id');
  if (tenant) {
    config.headers['X-Tenant'] = tenant;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {   
    sanitizeErrorResponse(error);

    const originalRequest = error.config || {};
    const isStudentPortalRequest = originalRequest.url?.startsWith('/student-portal/');

    // Prevent infinite loops
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // Student portal: expired/invalid session — clear student keys and bounce
    // to the student login, never the staff one. A failed *login* itself
    // (wrong credentials) is left alone so the component's own error state
    // handles it inline, without leaving /student/login.
    if (error.response?.status === 401 && isStudentPortalRequest && originalRequest.url !== '/student-portal/login') {
      originalRequest._retry = true;
      localStorage.removeItem('student_portal_token');
      localStorage.removeItem('student_portal_user');
      localStorage.removeItem('student_portal_expires');
      window.location.href = '/student/login';
      return Promise.reject(error);
    }

    // Skip redirect if the request is logout or login, platform admin, or student portal routes
    if (error.response?.status === 401 &&
        originalRequest.url !== '/logout' &&
        originalRequest.url !== '/login' &&
        originalRequest.url !== '/platform/login' &&
        !originalRequest.url?.startsWith('/platform/') &&
        !originalRequest.url?.startsWith('/central/') &&
        !isStudentPortalRequest &&
        !originalRequest._retry) {
      originalRequest._retry = true;

      // If we're in an impersonation session, close the tab instead of redirecting to login
      const impersonation = (() => {
        try { return JSON.parse(sessionStorage.getItem('impersonation_session')); } catch { return null; }
      })();

      if (impersonation) {
        sessionStorage.clear();
        window.close();
      } else {
        const tenantId = localStorage.getItem('tenant_id');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        localStorage.removeItem('token_expires_at');
        localStorage.removeItem('tenant_id');
        window.location.href = tenantId ? `/login?school=${encodeURIComponent(tenantId)}` : '/login';
      }
    }

    if (error.response?.status === 403 && error.response?.data?.suspended) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('permissions');
      localStorage.removeItem('token_expires_at');
      window.location.href = '/suspended';
    }

    return Promise.reject(error);
  }
);

export const tenantAPI = {
  provision: (data) => api.post('/tenants/provision', data),
  provisionStatus: (code) => api.get(`/tenants/provision/status/${code}`),
  getAll: (config) => api.get('/central/tenants', config),
  getById: (id, config) => api.get(`/central/tenants/${id}`, config),
  toggleStatus: (id, data, config) => api.patch(`/central/tenants/${id}/toggle-status`, data, config),
  delete: (id, config) => api.delete(`/central/tenants/${id}`, config),
  getAccounting: (id, config) => api.get(`/central/tenants/${id}/accounting`, config),
  saveAccounting: (id, data, config) => api.put(`/central/tenants/${id}/accounting`, data, config),
  provisionAccounting: (id, config) => api.post(`/central/tenants/${id}/accounting/provision`, {}, config),
  finishAccounting: (id, config) => api.post(`/central/tenants/${id}/accounting/finish`, {}, config),
};

export const platformAdminAPI = {
  login: (credentials) => api.post('/platform/login', credentials),
  logout: (config) => api.post('/platform/logout', {}, config),
  me: (config) => api.get('/platform/me', config),
  impersonate: (tenantId, config) => api.post(`/central/tenants/${tenantId}/impersonate`, {}, config),
};

// SchoolPay settings, managed centrally per-tenant by platform admins
export const centralSchoolPayAPI = {
  getSettings: (tenantId, config) => api.get(`/central/tenants/${tenantId}/schoolpay-settings`, config),
  saveSettings: (tenantId, data, config) => api.post(`/central/tenants/${tenantId}/schoolpay-settings`, data, config),
};


export const authAPI = {
  login: (credentials, config = {}) => api.post('/login', credentials, config),
  loginWithPhone: (credentials) => api.post('/login-phone', credentials),
  logout: () => api.post('/logout').catch(error => {
    console.warn('Logout API call failed:', error);
    return Promise.resolve();
  }),
  logoutAll: () => api.post('/logout-all'),
  refresh: () => api.post('/refresh'),
  getUser: () => api.get('/user'),
  updateProfile: (data) => api.put('/profile', data),
  changePassword: (data) => api.put('/profile/password', data),
  
  // Password reset with code
  sendResetCode: (data) => api.post('/send-reset-code', data),
  verifyResetCode: (data) => api.post('/verify-reset-code', data),
  resetPasswordWithCode: (data) => api.post('/reset-password-with-code', data),
  
  // Phone password reset
  sendPhoneResetCode: (data) => api.post('/send-phone-reset-code', data),
  verifyPhoneResetCode: (data) => api.post('/verify-phone-reset-code', data),
  resetPasswordWithPhoneCode: (data) => api.post('/reset-password-with-phone-code', data),
  
  // Email verification
  verifyEmail: (data) => api.post('/verify-email', data),
  confirmEmailVerification: (data) => api.post('/confirm-email-verification', data),
  resendVerification: (data) => api.post('/resend-verification', data),
  
  // Phone verification
  verifyPhone: (data) => api.post('/verify-phone', data),
  confirmPhoneVerification: (data) => api.post('/confirm-phone-verification', data),
  resendPhoneVerification: (data) => api.post('/resend-phone-verification', data),
};

export const studentAPI = {
  getAll: (params = {}) => api.get('/students', { params }),
  getActive: (params = {}) => api.get('/students', { params: { ...params, active: 1 } }),
  getById: (id) => api.get(`/students/${id}`),
  create: (data) => {
    const headers = data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {};
    return api.post('/students', data, { headers });
  },
  update: (id, data) => {
    const headers = data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {};
    return api.put(`/students/${id}`, data, { headers });
  },
  updateWithFile: (id, formData) => {
    return api.post(`/students/${id}`, formData, { 
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadProfilePicture: (id, formData) => {
    return api.post(`/students/${id}/profile-picture`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteProfilePicture: (id) => api.delete(`/students/${id}/profile-picture`),
  delete: (id) => api.delete(`/students/${id}`),
  bulkCreate: (data) => api.post('/students/bulk', data),
  search: (params = {}) => api.get('/students/search', { params }),
  
  // Classes and Streams
  getClasses: () => api.get('/academic/all-classes'),
  getStreams: (classId) => api.get('/students/class-streams', {
    params: classId ? { class_id: classId } : {}
  }),
  
  // Import/Export with proper blob handling
  exportExcel: (params = {}) => {
    const { format = 'csv', ...filters } = params;
    const finalParams = { format, ...filters };
    console.log('API exportExcel called with:');
    console.log('- Original params:', params);
    console.log('- Format:', format);
    console.log('- Filters:', filters);
    console.log('- Final API params:', finalParams);
    
    return api.get('/students/export', { 
      params: finalParams,
      responseType: 'blob',
      headers: {
        'Accept': format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv'
      }
    });
  },
  
  importExcel: (formData) => {
    console.log('Importing students...');
    return api.post('/students/import', formData, {
      headers: { 
        'Content-Type': 'multipart/form-data',
        'Accept': 'application/json'
      },
    });
  },
  
  downloadTemplate: () => {
    console.log('Downloading student import template...');
    return api.get('/students/template', { 
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
  },
  
  syncClasses: () => api.get('/students/import-options'),

  // Get student ledger with filters
  getLedger: (id, params = {}) => api.get(`/students/${id}/`,{params}),

  // Download ledger PDF
  downloadLedgerPdf: (id, params = {}) => {
    console.log('Downloading student ledger PDF...', params);
    return api.get(`/students/${id}/ledger/download`, {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  downloadStudentsPdf: (params = {}) => {
    console.log('Downloading students list PDF...', params);
    return api.get('/students/download-pdf', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  // Student Promotion
  getStudentsForPromotion: (classStreamId) => api.get('/students/promotion/by-class-stream', {
    params: { class_stream_id: classStreamId }
  }),
  previewPromotion: (data) => api.post('/students/promotion/preview', data),
  executePromotion: (data) => api.post('/students/promotion/execute', data),

  // Bulk Status Update
  bulkUpdateStatus: (data) => api.post('/students/bulk-status', data),
};

export const feeAPI = {
  getDashboard: () => api.get('/fees/dashboard'),
  getFinancialSummary: () => api.get('/fees/dashboard'),
  getReports: (params) => api.get('/fees/reports', { params }),

  // Fee Types
  getTypes: (params = {}) => api.get('/fees/types', { params }),
  createType: (data) => api.post('/fees/types', data),
  updateType: (id, data) => api.put(`/fees/types/${id}`, data),
  deleteType: (id) => api.delete(`/fees/types/${id}`),


  // Fee Groups
  getGroups: (params = {}) => api.get('/fees/groups', { params }),
  createGroup: (data) => api.post('/fees/groups', data),
  updateGroup: (id, data) => api.put(`/fees/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/fees/groups/${id}`),
  getStudentFeeGroups: (params = {}) => api.get('/fees/student-fee-groups', { params }),
  downloadGroupsPdf: (params = {}) => {
    console.log('Downloading fee groups PDF...', params);
    return api.get('/fees/groups/download', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  // Get students for allocation from fee group
  getStudentsForAllocation: (params = {}) => api.get('/fees/students-for-allocation', { params }),
  
  // Bulk allocate to selected students
  bulkAllocateToStudents: (data) => api.post('/fees/bulk-allocate', data),
  
  // Individual student allocation
  allocateToStudent: (data) => api.post('/fees/allocate-student', data),

  // Fee Allocations (view only)
  getAllocations: (params = {}) => api.get('/fees/allocations', { params }),
  downloadAllocationsPdf: (params = {}) => {
    console.log('Downloading fee allocations PDF...', params);
    return api.get('/fees/allocations/download', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  getClassesForFeeGroups: () => api.get('/fees/classes-for-groups'),

  // Fee Payments
  getPayments: (params = {}) => api.get('/fees/payments', { params }),
  createPayment: (data) => api.post('/fees/payments', data),

  // Payments & Transactions (manual + SchoolPay, read-only)
  getTransactions: (params = {}) => api.get('/fees/transactions', { params }),
  exportTransactions: (params = {}) => api.get('/fees/transactions/export', {
    params,
    responseType: 'blob',
    headers: { 'Accept': 'text/csv' },
  }),
  getAccounts: () => api.get('/fees/accounts'),
  downloadPaymentsPdf: (params = {}) => {
    console.log('Downloading fee payments PDF...', params);
    return api.get('/fees/payments/download', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  // Send Reminders
  sendReminders: () => api.post('/fees/send-reminders'),

  // Download Reports PDF
  downloadReportsPdf: (params = {}) => {
    console.log('Downloading fee reports PDF...', params);
    return api.get('/fees/reports/download', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  // Student Fee Card
  getStudentFeeCard: (params) => api.get('/fees/student-fee-card', { params }),
  
  // Terms
  getTerms: () => api.get('/fees/terms'),
  
  // Class Streams
  getClassStreams: () => api.get('/academic/class-streams'),
};

// ledger (real accounting API via Laravel gateway:
// {VERB} /accounting/ledger/{section}/{ledger-path})
const ledger = {
  get: (section, path, params = {}) => api.get(`/accounting/ledger/${section}/${path}`, { params }),
  post: (section, path, data = {}) => api.post(`/accounting/ledger/${section}/${path}`, data),
  put: (section, path, data = {}) => api.put(`/accounting/ledger/${section}/${path}`, data),
  patch: (section, path, data = {}) => api.patch(`/accounting/ledger/${section}/${path}`, data),
  remove: (section, path) => api.delete(`/accounting/ledger/${section}/${path}`),
  // Report/export routes stream a raw CSV/XLSX body (see
  // LedgerGatewayController::proxy) - never the {success,data} JSON
  // envelope - so this skips response parsing and hands back the blob.
  download: (section, path, params = {}) =>
    api.get(`/accounting/ledger/${section}/${path}`, { params, responseType: 'blob' }),
};

export const ledgerAPI = {
  // generic escape hatch (section + ledger path, e.g. 'coa', 'general-ledger/accounts')
  ...ledger,
  // --- Chart of Accounts (foundation: classes -> types -> sub-types -> accounts) ---
  // Classes own the code fence; types divide it; sub-types imply ranges; accounts
  // auto-generate codes from the sub-type slot unless overridden.
  getAccountClasses: (params = {}) => ledger.get('coa', 'general-ledger/account-classes', params),
  getAccountClass: (id) => ledger.get('coa', `general-ledger/account-classes/${id}`),
  createAccountClass: (data) => ledger.post('coa', 'general-ledger/account-classes', data),
  updateAccountClass: (id, data) => ledger.patch('coa', `general-ledger/account-classes/${id}`, data),
  deleteAccountClass: (id) => ledger.remove('coa', `general-ledger/account-classes/${id}`),
  getAccountTypes: (params = {}) => ledger.get('coa', 'general-ledger/account-types', params),
  getAccountType: (id) => ledger.get('coa', `general-ledger/account-types/${id}`),
  createAccountType: (data) => ledger.post('coa', 'general-ledger/account-types', data),
  updateAccountType: (id, data) => ledger.patch('coa', `general-ledger/account-types/${id}`, data),
  deleteAccountType: (id) => ledger.remove('coa', `general-ledger/account-types/${id}`),
  lockAccountType: (id) => ledger.patch('coa', `general-ledger/account-types/${id}/lock`),
  unlockAccountType: (id) => ledger.patch('coa', `general-ledger/account-types/${id}/unlock`),
  activateAccountType: (id) => ledger.patch('coa', `general-ledger/account-types/${id}/activate`),
  deactivateAccountType: (id) => ledger.patch('coa', `general-ledger/account-types/${id}/deactivate`),
  getAccountSubTypes: (params = {}) => ledger.get('coa', 'general-ledger/account-sub-types', params),
  getAccountSubType: (id) => ledger.get('coa', `general-ledger/account-sub-types/${id}`),
  getAccountSubTypesByType: (typeId) => ledger.get('coa', `general-ledger/account-sub-types/by-type/${typeId}`),
  createAccountSubType: (data) => ledger.post('coa', 'general-ledger/account-sub-types', data),
  updateAccountSubType: (id, data) => ledger.patch('coa', `general-ledger/account-sub-types/${id}`, data),
  deleteAccountSubType: (id) => ledger.remove('coa', `general-ledger/account-sub-types/${id}`),
  lockAccountSubType: (id) => ledger.patch('coa', `general-ledger/account-sub-types/${id}/lock`),
  unlockAccountSubType: (id) => ledger.patch('coa', `general-ledger/account-sub-types/${id}/unlock`),
  activateAccountSubType: (id) => ledger.patch('coa', `general-ledger/account-sub-types/${id}/activate`),
  deactivateAccountSubType: (id) => ledger.patch('coa', `general-ledger/account-sub-types/${id}/deactivate`),
  getAccounts: (params = {}) => ledger.get('coa', 'general-ledger/accounts', params),
  getAccount: (id) => ledger.get('coa', `general-ledger/accounts/${id}`),
  getAccountByCode: (code) => ledger.get('coa', `general-ledger/accounts/by-code/${code}`),
  getAccountChildren: (id) => ledger.get('coa', `general-ledger/accounts/${id}/children`),
  createAccount: (data) => ledger.post('coa', 'general-ledger/accounts', data),
  createChildAccount: (data) => ledger.post('coa', 'general-ledger/accounts/child', data),
  updateAccount: (id, data) => ledger.patch('coa', `general-ledger/accounts/${id}`, data),
  reparentAccount: (id, data) => ledger.patch('coa', `general-ledger/accounts/${id}/reparent`, data),
  deleteAccount: (id) => ledger.remove('coa', `general-ledger/accounts/${id}`),
  activateAccount: (id) => ledger.patch('coa', `general-ledger/accounts/${id}/activate`),
  deactivateAccount: (id) => ledger.patch('coa', `general-ledger/accounts/${id}/deactivate`),
  // COA templates are read-only seed data for onboarding.
  getCoaTemplates: (params = {}) => ledger.get('coa', 'general-ledger/coa-templates', params),
  getActiveCoaTemplates: () => ledger.get('coa', 'general-ledger/coa-templates/active'),
  getCoaTemplate: (id) => ledger.get('coa', `general-ledger/coa-templates/${id}`),
  // --- General ledger ---
  // Entries are always created DRAFT and must balance; only DRAFT entries are
  // editable. approve/post/void/reverse run as checker (APPROVE permission).
  getJournals: (params = {}) => ledger.get('gl', 'journal-entries', params),
  getJournal: (id) => ledger.get('gl', `journal-entries/${id}`),
  getJournalByNumber: (number) => ledger.get('gl', `journal-entries/by-entry-number/${number}`),
  getJournalsByPeriod: (periodId, params = {}) => ledger.get('gl', `journal-entries/by-period/${periodId}`, params),
  getAccountLedger: (accountId, params = {}) => ledger.get('gl', `journal-entries/account-ledger/${accountId}`, params),
  getJournalLines: (id) => ledger.get('gl', `journal-entries/${id}/lines`),
  createJournal: (data) => ledger.post('gl', 'journal-entries', data),
  updateJournal: (id, data) => ledger.patch('gl', `journal-entries/${id}`, data),
  updateJournalLines: (id, data) => ledger.patch('gl', `journal-entries/${id}/lines`, data),
  addJournalLine: (id, data) => ledger.post('gl', `journal-entries/${id}/lines`, data),
  deleteJournalLine: (id, lineId) => ledger.remove('gl', `journal-entries/${id}/lines/${lineId}`),
  deleteJournal: (id) => ledger.remove('gl', `journal-entries/${id}`),
  approveJournal: (id, data = {}) => ledger.patch('gl', `journal-entries/${id}/approve`, data),
  postJournal: (id, data = {}) => ledger.patch('gl', `journal-entries/${id}/post`, data),
  voidJournal: (id, data) => ledger.patch('gl', `journal-entries/${id}/void`, data),
  reverseJournal: (id, data = {}) => ledger.post('gl', `journal-entries/${id}/reverse`, data),
  bulkPostJournals: (data) => ledger.post('gl', 'journal-entries/bulk/post', data),
  bulkDeleteJournals: (data) => ledger.post('gl', 'journal-entries/bulk/delete', data),
  getTrialBalance: (params = {}) => ledger.get('gl', 'journal-entries/trial-balance', params),
  // Maker-checker for reversals: makers request, checkers approve/reject.
  createReversalRequest: (data) => ledger.post('gl', 'general-ledger/reversal-requests', data),
  getReversalRequests: (params = {}) => ledger.get('gl', 'general-ledger/reversal-requests', params),
  getReversalRequest: (id) => ledger.get('gl', `general-ledger/reversal-requests/${id}`),
  approveReversalRequest: (id, data = {}) => ledger.post('gl', `general-ledger/reversal-requests/${id}/approve`, data),
  rejectReversalRequest: (id, data = {}) => ledger.post('gl', `general-ledger/reversal-requests/${id}/reject`, data),
  cancelReversalRequest: (id) => ledger.post('gl', `general-ledger/reversal-requests/${id}/cancel`),
  // Recurring journals: templates that generate DRAFT entries when due.
  createRecurringJournal: (data) => ledger.post('gl', 'general-ledger/recurring-journals', data),
  previewRecurringJournals: (data = {}) => ledger.post('gl', 'general-ledger/recurring-journals/preview', data),
  runRecurringJournals: (data = {}) => ledger.post('gl', 'general-ledger/recurring-journals/run', data),
  getRecurringJournals: (params = {}) => ledger.get('gl', 'general-ledger/recurring-journals', params),
  getRecurringJournal: (id) => ledger.get('gl', `general-ledger/recurring-journals/${id}`),
  updateRecurringJournal: (id, data) => ledger.patch('gl', `general-ledger/recurring-journals/${id}`, data),
  pauseRecurringJournal: (id) => ledger.post('gl', `general-ledger/recurring-journals/${id}/pause`),
  resumeRecurringJournal: (id) => ledger.post('gl', `general-ledger/recurring-journals/${id}/resume`),
  deleteRecurringJournal: (id) => ledger.remove('gl', `general-ledger/recurring-journals/${id}`),
  // Account balances: per-account x period snapshots; rebuilt from posted lines.
  getAccountBalances: (params = {}) => ledger.get('gl', 'account-balances', params),
  getAccountBalance: (id) => ledger.get('gl', `account-balances/${id}`),
  getBalancesByAccount: (accountId, params = {}) => ledger.get('gl', `account-balances/by-account/${accountId}`, params),
  getLatestBalanceByAccount: (accountId) => ledger.get('gl', `account-balances/by-account/${accountId}/latest`),
  getBalanceSummaryByAccount: (accountId) => ledger.get('gl', `account-balances/by-account/${accountId}/summary`),
  getBalanceByAccountPeriod: (accountId, periodId) => ledger.get('gl', `account-balances/by-account/${accountId}/period/${periodId}`),
  getBalancesByPeriod: (periodId, params = {}) => ledger.get('gl', `account-balances/by-period/${periodId}`, params),
  getBalanceSummaryByPeriod: (periodId) => ledger.get('gl', `account-balances/by-period/${periodId}/summary`),
  getBalancesByFiscalYear: (fiscalYearId, params = {}) => ledger.get('gl', `account-balances/by-fiscal-year/${fiscalYearId}`, params),
  updateAccountBalance: (id, data) => ledger.patch('gl', `account-balances/${id}`, data),
  deleteAccountBalance: (id) => ledger.remove('gl', `account-balances/${id}`),
  initializeBalances: (data) => ledger.post('gl', 'account-balances/initialize', data),
  recalculateBalances: (periodId) => ledger.post('gl', `account-balances/recalculate/${periodId}`),
  // Read-only lookups for journal line dimension pickers (customer/supplier/
  // product autocomplete) — reachable from the GL section too, see
  // config/ledger.php's 'gl' allow list.
  getCustomers: (params = {}) => ledger.get('gl', 'party-sync/customers', params),
  getSuppliers: (params = {}) => ledger.get('gl', 'party-sync/suppliers', params),
  getInventoryItems: (params = {}) => ledger.get('gl', 'inventory/items', params),
  // Source-document lookups for the journal "link to source" picker.
  getArInvoices: (params = {}) => ledger.get('gl', 'accounts-receivable/invoices', params),
  getApInvoices: (params = {}) => ledger.get('gl', 'accounts-payable/invoices', params),
  // Subledger reconciliation: GL <-> subledger tie-out with manual adjustments.
  getSubledgerRecons: (params = {}) => ledger.get('gl', 'general-ledger/subledger-recons', params),
  getSubledgerRecon: (id) => ledger.get('gl', `general-ledger/subledger-recons/${id}`),
  createSubledgerRecon: (data) => ledger.post('gl', 'general-ledger/subledger-recons', data),
  refreshSubledgerRecon: (id) => ledger.post('gl', `general-ledger/subledger-recons/${id}/refresh`),
  addManualReconLine: (id, data) => ledger.post('gl', `general-ledger/subledger-recons/${id}/manual-lines`, data),
  addScheduleReconLine: (id, data) => ledger.post('gl', `general-ledger/subledger-recons/${id}/schedule-lines`, data),
  updateReconLine: (lineId, data) => ledger.post('gl', `general-ledger/subledger-recons/lines/${lineId}`, data),
  deleteReconLine: (lineId) => ledger.remove('gl', `general-ledger/subledger-recons/lines/${lineId}`),
  approveSubledgerRecon: (id, data = {}) => ledger.post('gl', `general-ledger/subledger-recons/${id}/approve`, data),
  reopenSubledgerRecon: (id) => ledger.post('gl', `general-ledger/subledger-recons/${id}/reopen`),
  completeSubledgerRecon: (id) => ledger.post('gl', `general-ledger/subledger-recons/${id}/complete`),
  deleteSubledgerRecon: (id) => ledger.remove('gl', `general-ledger/subledger-recons/${id}`),
  // --- Cash & Banking (Laravel gateway section 'cash' -> accounting-apis
  // cash-and-banking module) ---
  // Banking overview: read-only aggregate of every GL account linked to a
  // reconciliation-accounts detail (balance + reconciliation status).
  getBankingAccounts: (params = {}) => ledger.get('cash', 'banking/accounts', params),
  getBankingAccountDetail: (accountId, params = {}) => ledger.get('cash', `banking/accounts/${accountId}`, params),
  // Reconciliation account details: links a COA account to bank/mobile-money
  // identifiers + a payment method type (drives which fields are required).
  getReconciliationAccounts: (params = {}) => ledger.get('cash', 'reconciliation-accounts', params),
  getReconciliationAccount: (id) => ledger.get('cash', `reconciliation-accounts/${id}`),
  getReconciliationAccountByAccount: (accountId) => ledger.get('cash', `reconciliation-accounts/by-account/${accountId}`),
  createReconciliationAccount: (data) => ledger.post('cash', 'reconciliation-accounts', data),
  updateReconciliationAccount: (id, data) => ledger.patch('cash', `reconciliation-accounts/${id}`, data),
  deleteReconciliationAccount: (id) => ledger.remove('cash', `reconciliation-accounts/${id}`),
  // Payment method types: bank/mobile-money sub-categories under a payment
  // method, each declaring which identifier fields it requires.
  getPaymentMethodTypes: (params = {}) => ledger.get('cash', 'payment-method-types', params),
  getPaymentMethodType: (id) => ledger.get('cash', `payment-method-types/${id}`),
  createPaymentMethodType: (data) => ledger.post('cash', 'payment-method-types', data),
  updatePaymentMethodType: (id, data) => ledger.patch('cash', `payment-method-types/${id}`, data),
  deletePaymentMethodType: (id) => ledger.remove('cash', `payment-method-types/${id}`),
  // Bank transfers: DRAFT -> APPROVED -> POSTED (DR destination / CR source);
  // VOID reverses the journal once posted.
  getBankTransfers: (params = {}) => ledger.get('cash', 'cash/transfers', params),
  getBankTransfer: (id) => ledger.get('cash', `cash/transfers/${id}`),
  createBankTransfer: (data) => ledger.post('cash', 'cash/transfers', data),
  approveBankTransfer: (id) => ledger.post('cash', `cash/transfers/${id}/approve`),
  postBankTransfer: (id) => ledger.post('cash', `cash/transfers/${id}/post`),
  voidBankTransfer: (id, data) => ledger.post('cash', `cash/transfers/${id}/void`, data),
  // Cheques: ISSUED -> CLEARED (posts) or BOUNCED (reverses if cleared) or
  // CANCELLED (unpresented only).
  getCheques: (params = {}) => ledger.get('cash', 'cash/cheques', params),
  getCheque: (id) => ledger.get('cash', `cash/cheques/${id}`),
  issueCheque: (data) => ledger.post('cash', 'cash/cheques', data),
  clearCheque: (id, data = {}) => ledger.post('cash', `cash/cheques/${id}/clear`, data),
  bounceCheque: (id, data) => ledger.post('cash', `cash/cheques/${id}/bounce`, data),
  cancelCheque: (id) => ledger.post('cash', `cash/cheques/${id}/cancel`),
  // Petty cash: funds carry a float; expenses are DRAFT -> APPROVED -> POSTED
  // (DR expense / CR fund); establish/replenish post directly (DR fund / CR bank).
  getPettyCashFunds: () => ledger.get('cash', 'cash/petty-cash/funds'),
  getPettyCashFund: (id) => ledger.get('cash', `cash/petty-cash/funds/${id}`),
  getPettyCashExpenses: (fundId) => ledger.get('cash', `cash/petty-cash/funds/${fundId}/expenses`),
  createPettyCashFund: (data) => ledger.post('cash', 'cash/petty-cash/funds', data),
  establishPettyCashFund: (id, data) => ledger.post('cash', `cash/petty-cash/funds/${id}/establish`, data),
  replenishPettyCashFund: (id, data) => ledger.post('cash', `cash/petty-cash/funds/${id}/replenish`, data),
  createPettyCashExpense: (fundId, data) => ledger.post('cash', `cash/petty-cash/funds/${fundId}/expenses`, data),
  approvePettyCashExpense: (txnId) => ledger.post('cash', `cash/petty-cash/expenses/${txnId}/approve`),
  postPettyCashExpense: (txnId) => ledger.post('cash', `cash/petty-cash/expenses/${txnId}/post`),
  // Cash vouchers: standalone receipts/payments not tied to AP/AR (over-the-
  // counter sales, sundry income, direct expenses). DRAFT -> APPROVED -> POSTED.
  getCashVouchers: (params = {}) => ledger.get('cash', 'cash/vouchers', params),
  getCashVoucher: (id) => ledger.get('cash', `cash/vouchers/${id}`),
  getCashVoucherByNumber: (number) => ledger.get('cash', `cash/vouchers/by-number/${number}`),
  createCashVoucher: (data) => ledger.post('cash', 'cash/vouchers', data),
  approveCashVoucher: (id) => ledger.post('cash', `cash/vouchers/${id}/approve`),
  postCashVoucher: (id) => ledger.post('cash', `cash/vouchers/${id}/post`),
  voidCashVoucher: (id, data) => ledger.post('cash', `cash/vouchers/${id}/void`, data),
  deleteCashVoucher: (id) => ledger.remove('cash', `cash/vouchers/${id}`),
  // Bank statements: manual entry or CSV import, then an assisted matching
  // engine ties lines to book journal lines before pushing to a reconciliation.
  getBankStatements: (params = {}) => ledger.get('cash', 'cash-and-banking/bank-statements', params),
  getBankStatement: (id) => ledger.get('cash', `cash-and-banking/bank-statements/${id}`),
  createBankStatement: (data) => ledger.post('cash', 'cash-and-banking/bank-statements', data),
  importBankStatementCsv: (data) => ledger.post('cash', 'cash-and-banking/bank-statements/import-csv', data),
  previewBankStatementCsv: (data) => ledger.post('cash', 'cash-and-banking/bank-statements/preview-csv', data),
  addBankStatementLines: (id, data) => ledger.post('cash', `cash-and-banking/bank-statements/${id}/lines`, data),
  deleteBankStatementLine: (lineId) => ledger.remove('cash', `cash-and-banking/bank-statements/lines/${lineId}`),
  autoMatchBankStatement: (id, data = {}) => ledger.post('cash', `cash-and-banking/bank-statements/${id}/auto-match`, data),
  confirmStatementLineMatch: (lineId, data) => ledger.post('cash', `cash-and-banking/bank-statements/lines/${lineId}/confirm`, data),
  unmatchStatementLine: (lineId) => ledger.post('cash', `cash-and-banking/bank-statements/lines/${lineId}/unmatch`),
  resolveStatementLine: (lineId, data) => ledger.post('cash', `cash-and-banking/bank-statements/lines/${lineId}/resolve`, data),
  pushStatementToRecon: (id) => ledger.post('cash', `cash-and-banking/bank-statements/${id}/push-to-recon`),
  deleteBankStatement: (id) => ledger.remove('cash', `cash-and-banking/bank-statements/${id}`),
  // Match rules: per-account auto-match / suggest-charge / suggest-interest
  // rules the matching engine consults in priority order.
  getMatchRules: (params = {}) => ledger.get('cash', 'cash-and-banking/match-rules', params),
  getMatchRule: (id) => ledger.get('cash', `cash-and-banking/match-rules/${id}`),
  createMatchRule: (data) => ledger.post('cash', 'cash-and-banking/match-rules', data),
  updateMatchRule: (id, data) => ledger.patch('cash', `cash-and-banking/match-rules/${id}`, data),
  deleteMatchRule: (id) => ledger.remove('cash', `cash-and-banking/match-rules/${id}`),
  // --- Accounts Receivable (Laravel gateway section 'receivables' ->
  // accounting-apis accounts-receivable / customer-ledger / subsidiary-ledger
  // modules). Student billing convention (see SchoolAccounting.php):
  // customer_id = schoolsync students.id, ext_ref = registration_no, one
  // invoice per (student, fee group) bill, one receipt per FeePayment.
  // Invoices/receipts follow the same DRAFT -> APPROVED -> POSTED lifecycle
  // as journals/cash vouchers (approve + void run as checker, create/post
  // as maker - see config/ledger.php checker_suffixes).
  getReceivableInvoices: (params = {}) => ledger.get('receivables', 'accounts-receivable/invoices', params),
  getReceivableInvoice: (id) => ledger.get('receivables', `accounts-receivable/invoices/${id}`),
  createReceivableInvoice: (data) => ledger.post('receivables', 'accounts-receivable/invoices', data),
  approveReceivableInvoice: (id) => ledger.post('receivables', `accounts-receivable/invoices/${id}/approve`),
  postReceivableInvoice: (id) => ledger.post('receivables', `accounts-receivable/invoices/${id}/post`),
  voidReceivableInvoice: (id, data = {}) => ledger.post('receivables', `accounts-receivable/invoices/${id}/void`, data),
  deleteReceivableInvoice: (id) => ledger.remove('receivables', `accounts-receivable/invoices/${id}`),
  // Debtor aging: bucketed totals (current/1-30/31-60/61-90/90+) - same
  // endpoint AccountingController::dashboard() calls for the "Ledger
  // Receivable" KPI, reachable here via the invoices prefix already
  // whitelisted for this section.
  getDebtorAging: (params = {}) => ledger.get('receivables', 'accounts-receivable/invoices/debtor-aging', params),
  // Receipts: allocate a customer receipt across one or more open invoices.
  getReceivablePayments: (params = {}) => ledger.get('receivables', 'accounts-receivable/payments', params),
  getReceivablePayment: (id) => ledger.get('receivables', `accounts-receivable/payments/${id}`),
  createReceivablePayment: (data) => ledger.post('receivables', 'accounts-receivable/payments', data),
  approveReceivablePayment: (id) => ledger.post('receivables', `accounts-receivable/payments/${id}/approve`),
  postReceivablePayment: (id) => ledger.post('receivables', `accounts-receivable/payments/${id}/post`),
  voidReceivablePayment: (id, data = {}) => ledger.post('receivables', `accounts-receivable/payments/${id}/void`, data),
  deleteReceivablePayment: (id) => ledger.remove('receivables', `accounts-receivable/payments/${id}`),
  // Customer (student) subledger: one running-balance account per customer,
  // plus its posted entries (statement) - same shape family as
  // journal-entries/account-ledger/{accountId} for GL accounts.
  getCustomerLedgerAccounts: (params = {}) => ledger.get('receivables', 'customer-ledger/accounts', params),
  getCustomerLedgerAccount: (id, params = {}) => ledger.get('receivables', `customer-ledger/accounts/${id}`, params),
  getSubsidiaryLedgerEntries: (params = {}) => ledger.get('receivables', 'subsidiary-ledger/entries', params),
  // Provisions (bad-debt / doubtful-debt), write-offs and recoveries -
  // same DRAFT -> APPROVED -> POSTED lifecycle as the rest of this ledger;
  // field contracts are not exercised anywhere in this repo yet (no
  // AccountingClient.php equivalent), so these follow the established
  // naming convention defensively - verify against the live ledger before
  // relying on exact field names.
  getArProvisions: (params = {}) => ledger.get('receivables', 'accounts-receivable/provisions', params),
  getArProvision: (id) => ledger.get('receivables', `accounts-receivable/provisions/${id}`),
  createArProvision: (data) => ledger.post('receivables', 'accounts-receivable/provisions', data),
  approveArProvision: (id) => ledger.post('receivables', `accounts-receivable/provisions/${id}/approve`),
  postArProvision: (id) => ledger.post('receivables', `accounts-receivable/provisions/${id}/post`),
  voidArProvision: (id, data = {}) => ledger.post('receivables', `accounts-receivable/provisions/${id}/void`, data),
  deleteArProvision: (id) => ledger.remove('receivables', `accounts-receivable/provisions/${id}`),
  getArWriteOffs: (params = {}) => ledger.get('receivables', 'accounts-receivable/write-offs', params),
  getArWriteOff: (id) => ledger.get('receivables', `accounts-receivable/write-offs/${id}`),
  createArWriteOff: (data) => ledger.post('receivables', 'accounts-receivable/write-offs', data),
  approveArWriteOff: (id) => ledger.post('receivables', `accounts-receivable/write-offs/${id}/approve`),
  postArWriteOff: (id) => ledger.post('receivables', `accounts-receivable/write-offs/${id}/post`),
  voidArWriteOff: (id, data = {}) => ledger.post('receivables', `accounts-receivable/write-offs/${id}/void`, data),
  deleteArWriteOff: (id) => ledger.remove('receivables', `accounts-receivable/write-offs/${id}`),
  getArRecoveries: (params = {}) => ledger.get('receivables', 'accounts-receivable/recoveries', params),
  getArRecovery: (id) => ledger.get('receivables', `accounts-receivable/recoveries/${id}`),
  createArRecovery: (data) => ledger.post('receivables', 'accounts-receivable/recoveries', data),
  approveArRecovery: (id) => ledger.post('receivables', `accounts-receivable/recoveries/${id}/approve`),
  postArRecovery: (id) => ledger.post('receivables', `accounts-receivable/recoveries/${id}/post`),
  voidArRecovery: (id, data = {}) => ledger.post('receivables', `accounts-receivable/recoveries/${id}/void`, data),
  deleteArRecovery: (id) => ledger.remove('receivables', `accounts-receivable/recoveries/${id}`),
  // AP/AR reports - read-only, verified against accounting-apis
  // (subsidiary-ledger + customer-invoice modules). NOTE the ledger rejects
  // unknown query keys (forbidNonWhitelisted), so callers must send ONLY the
  // documented params: debtor-aging takes {currency_id?, customer_id?} (no
  // as_of_date - always as of now); payment-forecast takes {days_ahead?};
  // reconciliation/ar takes {period_id?}.
  // (ap-ar-reports/aging/debtors is a stub returning empty rows - the live
  // aging is accounts-receivable/invoices/debtor-aging, same feed the
  // Accounting Dashboard's "Ledger Receivable" KPI uses.)
  getArAgingReport: (params = {}) => ledger.get('receivables', 'accounts-receivable/invoices/debtor-aging', params),
  getArPaymentForecast: (params = {}) => ledger.get('receivables', 'ap-ar-reports/payment-forecast', params),
  getArReconciliationReport: (params = {}) => ledger.get('receivables', 'ap-ar-reports/reconciliation/ar', params),
  // --- Fees & Billing (Laravel gateway section 'fees') - the same
  // accounts-receivable/invoices + /payments + debtor-aging paths as the
  // 'receivables' section above, reached through a separate gateway
  // section so front-desk/billing staff can be granted RESOURCES.LEDGER.FEES
  // (bill + collect) without the accountant-level RESOURCES.LEDGER.RECEIVABLES
  // permissions (provisions/write-offs/recoveries/full AR reports) - see
  // config/ledger.php's 'fees' allow list. Same DTOs, same field contracts
  // as the receivables versions (verified against AccountingClient.php).
  getFeeInvoices: (params = {}) => ledger.get('fees', 'accounts-receivable/invoices', params),
  getFeeInvoice: (id) => ledger.get('fees', `accounts-receivable/invoices/${id}`),
  createFeeInvoice: (data) => ledger.post('fees', 'accounts-receivable/invoices', data),
  approveFeeInvoice: (id) => ledger.post('fees', `accounts-receivable/invoices/${id}/approve`),
  postFeeInvoice: (id) => ledger.post('fees', `accounts-receivable/invoices/${id}/post`),
  voidFeeInvoice: (id, data = {}) => ledger.post('fees', `accounts-receivable/invoices/${id}/void`, data),
  deleteFeeInvoice: (id) => ledger.remove('fees', `accounts-receivable/invoices/${id}`),
  getFeePayments: (params = {}) => ledger.get('fees', 'accounts-receivable/payments', params),
  getFeePayment: (id) => ledger.get('fees', `accounts-receivable/payments/${id}`),
  createFeePayment: (data) => ledger.post('fees', 'accounts-receivable/payments', data),
  approveFeePayment: (id) => ledger.post('fees', `accounts-receivable/payments/${id}/approve`),
  postFeePayment: (id) => ledger.post('fees', `accounts-receivable/payments/${id}/post`),
  voidFeePayment: (id, data = {}) => ledger.post('fees', `accounts-receivable/payments/${id}/void`, data),
  deleteFeePayment: (id) => ledger.remove('fees', `accounts-receivable/payments/${id}`),
  getFeeAgingReport: (params = {}) => ledger.get('fees', 'accounts-receivable/invoices/debtor-aging', params),
  // Recon-linked bank/cash accounts for the Fees Management receipt picker
  // (same banking/accounts resource as Cash & Banking, reached through the
  // 'fees' gateway section so bursars need no extra permission).
  getFeeBankAccounts: (params = {}) => ledger.get('fees', 'banking/accounts', params),
  // --- Accounts Payable (Laravel gateway section 'ap') - the AR module's
  // mirror image: supplier bills instead of customer invoices, payments
  // made instead of received. No AccountingClient.php equivalent exists for
  // AP (the school integration only bills/collects from students), so field
  // names follow the same conventions as the verified AR contract with the
  // obvious substitutions (customer_id -> supplier_id, revenue_account_id ->
  // expense_account_id) - verify against the live ledger before relying on
  // them. Note this section has no aging/reports prefix in config/ledger.php
  // (unlike 'receivables'/'fees'), so there's no AP aging report to call.
  getPayableInvoices: (params = {}) => ledger.get('ap', 'accounts-payable/invoices', params),
  getPayableInvoice: (id) => ledger.get('ap', `accounts-payable/invoices/${id}`),
  createPayableInvoice: (data) => ledger.post('ap', 'accounts-payable/invoices', data),
  approvePayableInvoice: (id) => ledger.post('ap', `accounts-payable/invoices/${id}/approve`),
  postPayableInvoice: (id) => ledger.post('ap', `accounts-payable/invoices/${id}/post`),
  voidPayableInvoice: (id, data = {}) => ledger.post('ap', `accounts-payable/invoices/${id}/void`, data),
  deletePayableInvoice: (id) => ledger.remove('ap', `accounts-payable/invoices/${id}`),
  getPayablePayments: (params = {}) => ledger.get('ap', 'accounts-payable/payments', params),
  getPayablePayment: (id) => ledger.get('ap', `accounts-payable/payments/${id}`),
  createPayablePayment: (data) => ledger.post('ap', 'accounts-payable/payments', data),
  approvePayablePayment: (id) => ledger.post('ap', `accounts-payable/payments/${id}/approve`),
  postPayablePayment: (id) => ledger.post('ap', `accounts-payable/payments/${id}/post`),
  voidPayablePayment: (id, data = {}) => ledger.post('ap', `accounts-payable/payments/${id}/void`, data),
  deletePayablePayment: (id) => ledger.remove('ap', `accounts-payable/payments/${id}`),
  // Payment batches: bundle several supplier payments (one bank debit,
  // several allocations) into a single maker-checker document.
  getPayableBatches: (params = {}) => ledger.get('ap', 'accounts-payable/payment-batches', params),
  getPayableBatch: (id) => ledger.get('ap', `accounts-payable/payment-batches/${id}`),
  createPayableBatch: (data) => ledger.post('ap', 'accounts-payable/payment-batches', data),
  approvePayableBatch: (id) => ledger.post('ap', `accounts-payable/payment-batches/${id}/approve`),
  postPayableBatch: (id) => ledger.post('ap', `accounts-payable/payment-batches/${id}/post`),
  voidPayableBatch: (id, data = {}) => ledger.post('ap', `accounts-payable/payment-batches/${id}/void`, data),
  deletePayableBatch: (id) => ledger.remove('ap', `accounts-payable/payment-batches/${id}`),
  // Supplier subledger: one running-balance account per supplier. No
  // separate subsidiary-ledger/entries prefix is whitelisted for 'ap'
  // (unlike 'receivables'), so the statement view reads getSupplierLedgerAccount's
  // own detail response - same embedded-entries shape as
  // journal-entries/account-ledger/{accountId} (verified in Journals.js).
  getSupplierLedgerAccounts: (params = {}) => ledger.get('ap', 'supplier-ledger/accounts', params),
  getSupplierLedgerAccount: (id, params = {}) => ledger.get('ap', `supplier-ledger/accounts/${id}`, params),
  // --- Projects and restricted-fund/job tracking (gateway section 'projects') ---
  getProjects: (params = {}) => ledger.get('projects', 'projects', params),
  getProject: (id) => ledger.get('projects', `projects/${id}`),
  createProject: (data) => ledger.post('projects', 'projects', data),
  updateProject: (id, data) => ledger.patch('projects', `projects/${id}`, data),
  activateProject: (id) => ledger.post('projects', `projects/${id}/activate`),
  deactivateProject: (id) => ledger.post('projects', `projects/${id}/deactivate`),
  getProjectBudgetLines: (params = {}) => ledger.get('projects', 'project-budget-lines', params),
  createProjectBudgetLine: (data) => ledger.post('projects', 'project-budget-lines', data),
  updateProjectBudgetLine: (id, data) => ledger.patch('projects', `project-budget-lines/${id}`, data),
  deleteProjectBudgetLine: (id) => ledger.remove('projects', `project-budget-lines/${id}`),
  submitProjectBudgetLine: (id) => ledger.post('projects', `project-budget-lines/${id}/submit`),
  approveProjectBudgetLine: (id) => ledger.post('projects', `project-budget-lines/${id}/approve`),
  rejectProjectBudgetLine: (id) => ledger.post('projects', `project-budget-lines/${id}/reject`),
  lockProjectBudgetLine: (id) => ledger.post('projects', `project-budget-lines/${id}/lock`),
  unlockProjectBudgetLine: (id) => ledger.post('projects', `project-budget-lines/${id}/unlock`),
  getProjectCosts: (id) => ledger.get('projects', `projects/${id}/costs`),
  getProjectWip: (id) => ledger.get('projects', `projects/${id}/wip`),
  getProjectProfitability: (id) => ledger.get('projects', `projects/${id}/profitability`),
  getProjectBudgetVsActual: (id, params = {}) => ledger.get('projects', `projects/${id}/budget-vs-actual`, params),
  // --- Costing: cost capture, dimensions, allocation and centre reports ---
  getCostCategories: (params = {}) => ledger.get('costing', 'costing/categories', params),
  createCostCategory: (data) => ledger.post('costing', 'costing/categories', data),
  updateCostCategory: (id, data) => ledger.patch('costing', `costing/categories/${id}`, data),
  deleteCostCategory: (id) => ledger.remove('costing', `costing/categories/${id}`),
  getCostUnits: (params = {}) => ledger.get('costing', 'costing/units', params),
  createCostUnit: (data) => ledger.post('costing', 'costing/units', data),
  updateCostUnit: (id, data) => ledger.patch('costing', `costing/units/${id}`, data),
  deleteCostUnit: (id) => ledger.remove('costing', `costing/units/${id}`),
  getCostActivities: (params = {}) => ledger.get('costing', 'costing/activities', params),
  createCostActivity: (data) => ledger.post('costing', 'costing/activities', data),
  updateCostActivity: (id, data) => ledger.patch('costing', `costing/activities/${id}`, data),
  deleteCostActivity: (id) => ledger.remove('costing', `costing/activities/${id}`),
  upsertCostStatistic: (data) => ledger.post('costing', 'costing/statistics', data),
  getCostEntries: (params = {}) => ledger.get('costing', 'costing/entries', params),
  getCostEntry: (id) => ledger.get('costing', `costing/entries/${id}`),
  createCostEntry: (data) => ledger.post('costing', 'costing/entries', data),
  approveCostEntry: (id) => ledger.post('costing', `costing/entries/${id}/approve`),
  postCostEntry: (id) => ledger.post('costing', `costing/entries/${id}/post`),
  reverseCostEntry: (id, data) => ledger.post('costing', `costing/entries/${id}/reverse`, data),
  deleteCostEntry: (id) => ledger.remove('costing', `costing/entries/${id}`),
  getCostAllocationRules: (params = {}) => ledger.get('costing', 'costing/rules', params),
  createCostAllocationRule: (data) => ledger.post('costing', 'costing/rules', data),
  updateCostAllocationRule: (id, data) => ledger.patch('costing', `costing/rules/${id}`, data),
  deleteCostAllocationRule: (id) => ledger.remove('costing', `costing/rules/${id}`),
  getCostAllocationRuns: (params = {}) => ledger.get('costing', 'costing/runs', params),
  getCostAllocationRun: (id) => ledger.get('costing', `costing/runs/${id}`),
  previewCostAllocation: (data) => ledger.post('costing', 'costing/runs/preview', data),
  createCostAllocationRun: (data) => ledger.post('costing', 'costing/runs', data),
  approveCostAllocationRun: (id) => ledger.post('costing', `costing/runs/${id}/approve`),
  postCostAllocationRun: (id) => ledger.post('costing', `costing/runs/${id}/post`),
  reverseCostAllocationRun: (id, data) => ledger.post('costing', `costing/runs/${id}/reverse`, data),
  cancelCostAllocationRun: (id) => ledger.post('costing', `costing/runs/${id}/cancel`),
  getCostCentreSummary: (params) => ledger.get('costing', 'costing/reports/centre-summary', params),
  // Read-only audit trail for accounting events (Laravel gateway section 'audit').
  getAuditLogs: (params = {}) => ledger.get('audit', 'audit-logs', params),
  // --- Accruals and deferrals (Laravel gateway section 'gl') ---
  getAccrualSchedules: (params = {}) => ledger.get('gl', 'accruals/schedules', params),
  getAccrualSchedule: (id) => ledger.get('gl', `accruals/schedules/${id}`),
  createAccrualSchedule: (data) => ledger.post('gl', 'accruals/schedules', data),
  updateAccrualSchedule: (id, data) => ledger.patch('gl', `accruals/schedules/${id}`, data),
  approveAccrualSchedule: (id) => ledger.post('gl', `accruals/schedules/${id}/approve`),
  postAccrualInitial: (id) => ledger.post('gl', `accruals/schedules/${id}/post-initial`),
  recognizeAccrual: (id, data = {}) => ledger.post('gl', `accruals/schedules/${id}/recognize`, data),
  cancelAccrualSchedule: (id) => ledger.post('gl', `accruals/schedules/${id}/cancel`),
  runAccruals: (data = {}) => ledger.post('gl', 'accruals/schedules/run', data),
  reverseAccrualRecognition: (id, data) => ledger.post('gl', `accruals/recognitions/${id}/reverse`, data),
  // --- Income / revenue recognition (Laravel gateway section 'income') -
  // deferred revenue: a lump sum is billed/collected once but recognised
  // into income over a schedule of periods. No AccountingClient.php
  // equivalent exists (the school integration recognises fee revenue
  // immediately on posting), so field names are this module's best-effort
  // domain convention - verify against the live ledger. OBJECT_TYPES in
  // configSections.js confirms 'REVENUE_SCHEDULE' is a real ledger concept.
  getRevenueSchedules: (params = {}) => ledger.get('income', 'revenue/schedules', params),
  getRevenueSchedule: (id) => ledger.get('income', `revenue/schedules/${id}`),
  createRevenueSchedule: (data) => ledger.post('income', 'revenue/schedules', data),
  approveRevenueSchedule: (id) => ledger.post('income', `revenue/schedules/${id}/approve`),
  postRevenueSchedule: (id) => ledger.post('income', `revenue/schedules/${id}/post`),
  voidRevenueSchedule: (id, data = {}) => ledger.post('income', `revenue/schedules/${id}/void`, data),
  deleteRevenueSchedule: (id) => ledger.remove('income', `revenue/schedules/${id}`),
  // Recognitions are the individual per-period postings a schedule
  // generates once posted - read + post only, no create/delete (the
  // schedule owns their lifecycle).
  getRevenueRecognitions: (params = {}) => ledger.get('income', 'revenue/recognitions', params),
  getRevenueRecognition: (id) => ledger.get('income', `revenue/recognitions/${id}`),
  postRevenueRecognition: (id) => ledger.post('income', `revenue/recognitions/${id}/post`),
  // Recurring revenue templates - same preview/run/pause/resume shape as
  // the verified general-ledger/recurring-journals (see Journals.js), but
  // this is a distinct, income-specific REST resource, not that engine.
  getRevenueRecurring: (params = {}) => ledger.get('income', 'revenue/recurring', params),
  getRevenueRecurringOne: (id) => ledger.get('income', `revenue/recurring/${id}`),
  createRevenueRecurring: (data) => ledger.post('income', 'revenue/recurring', data),
  updateRevenueRecurring: (id, data) => ledger.patch('income', `revenue/recurring/${id}`, data),
  previewRevenueRecurring: (data = {}) => ledger.post('income', 'revenue/recurring/preview', data),
  runRevenueRecurring: (data = {}) => ledger.post('income', 'revenue/recurring/run', data),
  pauseRevenueRecurring: (id) => ledger.post('income', `revenue/recurring/${id}/pause`),
  resumeRevenueRecurring: (id) => ledger.post('income', `revenue/recurring/${id}/resume`),
  deleteRevenueRecurring: (id) => ledger.remove('income', `revenue/recurring/${id}`),
  // --- Expenses (Laravel gateway section 'expenses') - employee expense
  // claims and their reimbursement, plus recurring operational expenses.
  // OBJECT_TYPES confirms 'EXPENSE_CLAIM'/'EXPENSE_REIMBURSEMENT' as real
  // ledger concepts; exact field names are still a best-effort convention -
  // verify against the live ledger.
  getExpenseCategories: (params = {}) => ledger.get('expenses', 'expenses/categories', params),
  getExpenseCategory: (id) => ledger.get('expenses', `expenses/categories/${id}`),
  createExpenseCategory: (data) => ledger.post('expenses', 'expenses/categories', data),
  updateExpenseCategory: (id, data) => ledger.patch('expenses', `expenses/categories/${id}`, data),
  deleteExpenseCategory: (id) => ledger.remove('expenses', `expenses/categories/${id}`),
  getExpenseClaims: (params = {}) => ledger.get('expenses', 'expenses/claims', params),
  getExpenseClaim: (id) => ledger.get('expenses', `expenses/claims/${id}`),
  createExpenseClaim: (data) => ledger.post('expenses', 'expenses/claims', data),
  approveExpenseClaim: (id) => ledger.post('expenses', `expenses/claims/${id}/approve`),
  voidExpenseClaim: (id, data = {}) => ledger.post('expenses', `expenses/claims/${id}/void`, data),
  deleteExpenseClaim: (id) => ledger.remove('expenses', `expenses/claims/${id}`),
  getExpenseReimbursements: (params = {}) => ledger.get('expenses', 'expenses/reimbursements', params),
  getExpenseReimbursement: (id) => ledger.get('expenses', `expenses/reimbursements/${id}`),
  createExpenseReimbursement: (data) => ledger.post('expenses', 'expenses/reimbursements', data),
  approveExpenseReimbursement: (id) => ledger.post('expenses', `expenses/reimbursements/${id}/approve`),
  postExpenseReimbursement: (id) => ledger.post('expenses', `expenses/reimbursements/${id}/post`),
  voidExpenseReimbursement: (id, data = {}) => ledger.post('expenses', `expenses/reimbursements/${id}/void`, data),
  deleteExpenseReimbursement: (id) => ledger.remove('expenses', `expenses/reimbursements/${id}`),
  getExpenseRecurring: (params = {}) => ledger.get('expenses', 'expenses/recurring', params),
  getExpenseRecurringOne: (id) => ledger.get('expenses', `expenses/recurring/${id}`),
  createExpenseRecurring: (data) => ledger.post('expenses', 'expenses/recurring', data),
  updateExpenseRecurring: (id, data) => ledger.patch('expenses', `expenses/recurring/${id}`, data),
  previewExpenseRecurring: (data = {}) => ledger.post('expenses', 'expenses/recurring/preview', data),
  runExpenseRecurring: (data = {}) => ledger.post('expenses', 'expenses/recurring/run', data),
  pauseExpenseRecurring: (id) => ledger.post('expenses', `expenses/recurring/${id}/pause`),
  resumeExpenseRecurring: (id) => ledger.post('expenses', `expenses/recurring/${id}/resume`),
  deleteExpenseRecurring: (id) => ledger.remove('expenses', `expenses/recurring/${id}`),
  getExpenseReports: (params = {}) => ledger.get('expenses', 'expenses/reports', params),
  // --- Budgeting (Laravel gateway section 'budgeting' -> accounting-apis
  // budgeting module). One row per (GL account x fiscal year x optional
  // period): DRAFT -> SUBMITTED -> APPROVED -> LOCKED (submit runs as maker;
  // approve/unapprove/reject/lock/unlock run as checker - see
  // config/ledger.php checker_suffixes). Field names verified against
  // accounting-apis src/modules/budgeting/budget/dto/index.ts.
  getBudgets: (params = {}) => ledger.get('budgeting', 'budgets', params),
  getBudget: (id) => ledger.get('budgeting', `budgets/${id}`),
  createBudget: (data) => ledger.post('budgeting', 'budgets', data),
  updateBudget: (id, data) => ledger.patch('budgeting', `budgets/${id}`, data),
  submitBudget: (id) => ledger.post('budgeting', `budgets/${id}/submit`),
  approveBudget: (id) => ledger.post('budgeting', `budgets/${id}/approve`),
  unapproveBudget: (id) => ledger.post('budgeting', `budgets/${id}/unapprove`),
  rejectBudget: (id) => ledger.post('budgeting', `budgets/${id}/reject`),
  lockBudget: (id) => ledger.post('budgeting', `budgets/${id}/lock`),
  unlockBudget: (id) => ledger.post('budgeting', `budgets/${id}/unlock`),
  deleteBudget: (id) => ledger.remove('budgeting', `budgets/${id}`),
  // Vs-actual requires fiscal_year_id; compares each line against posted
  // account balances. Variance = actual - budgeted (positive = overspend).
  getBudgetVsActual: (params = {}) => ledger.get('budgeting', 'budgets/vs-actual', params),
  // Transfers move budget between two APPROVED lines: DRAFT -> APPROVED ->
  // POSTED (atomic); DRAFT/APPROVED can be REJECTED. List is unpaginated.
  getBudgetTransfers: (params = {}) => ledger.get('budgeting', 'budgets/transfers/all', params),
  createBudgetTransfer: (data) => ledger.post('budgeting', 'budgets/transfers', data),
  approveBudgetTransfer: (id) => ledger.post('budgeting', `budgets/transfers/${id}/approve`),
  postBudgetTransfer: (id) => ledger.post('budgeting', `budgets/transfers/${id}/post`),
  rejectBudgetTransfer: (id) => ledger.post('budgeting', `budgets/transfers/${id}/reject`),
  // Rollover copies one fiscal year's lines into another as DRAFT and
  // returns {copied, skipped_duplicates} - single action, no listing.
  createBudgetRollover: (data) => ledger.post('budgeting', 'budgets/rollover', data),
  // --- Fixed Assets (Laravel gateway section 'assets' -> accounting-apis
  // fixed-assets module). Categories own the depreciation policy;
  // assets go DRAFT -> APPROVED -> ACTIVE (capitalise); disposal retires
  // them (DISPOSED/RETIRED). Runs go DRAFT -> APPROVED -> POSTED, with
  // cancel (DRAFT) and reverse (POSTED). Verified against
  // accounting-apis src/modules/fixed-assets/dto/index.ts.
  getAssetCategories: (params = {}) => ledger.get('assets', 'fixed-assets/categories', params),
  getAssetCategory: (id) => ledger.get('assets', `fixed-assets/categories/${id}`),
  createAssetCategory: (data) => ledger.post('assets', 'fixed-assets/categories', data),
  updateAssetCategory: (id, data) => ledger.patch('assets', `fixed-assets/categories/${id}`, data),
  deleteAssetCategory: (id) => ledger.remove('assets', `fixed-assets/categories/${id}`),
  getAssets: (params = {}) => ledger.get('assets', 'fixed-assets/assets', params),
  getAsset: (id) => ledger.get('assets', `fixed-assets/assets/${id}`),
  getAssetByNumber: (number) => ledger.get('assets', `fixed-assets/assets/by-number/${number}`),
  createAsset: (data) => ledger.post('assets', 'fixed-assets/assets', data),
  updateAsset: (id, data) => ledger.patch('assets', `fixed-assets/assets/${id}`, data),
  approveAsset: (id) => ledger.post('assets', `fixed-assets/assets/${id}/approve`),
  capitalizeAsset: (id, data) => ledger.post('assets', `fixed-assets/assets/${id}/capitalize`, data),
  transferAsset: (id, data = {}) => ledger.post('assets', `fixed-assets/assets/${id}/transfer`, data),
  deleteAsset: (id) => ledger.remove('assets', `fixed-assets/assets/${id}`),
  previewDepreciationRun: (data) => ledger.post('assets', 'fixed-assets/depreciation/preview', data),
  getDepreciationRuns: (params = {}) => ledger.get('assets', 'fixed-assets/depreciation/runs', params),
  getDepreciationRun: (id) => ledger.get('assets', `fixed-assets/depreciation/runs/${id}`),
  createDepreciationRun: (data) => ledger.post('assets', 'fixed-assets/depreciation/runs', data),
  approveDepreciationRun: (id) => ledger.post('assets', `fixed-assets/depreciation/runs/${id}/approve`),
  postDepreciationRun: (id) => ledger.post('assets', `fixed-assets/depreciation/runs/${id}/post`),
  reverseDepreciationRun: (id, data) => ledger.post('assets', `fixed-assets/depreciation/runs/${id}/reverse`, data),
  cancelDepreciationRun: (id) => ledger.post('assets', `fixed-assets/depreciation/runs/${id}/cancel`),
  revalueAsset: (id, data) => ledger.post('assets', `fixed-assets/depreciation/assets/${id}/revalue`, data),
  getAssetRevaluations: (id) => ledger.get('assets', `fixed-assets/depreciation/assets/${id}/revaluations`),
  disposeAsset: (id, data) => ledger.post('assets', `fixed-assets/depreciation/assets/${id}/dispose`, data),
  getDisposals: (params = {}) => ledger.get('assets', 'fixed-assets/depreciation/disposals', params),
  getDepreciationSummary: (params = {}) => ledger.get('assets', 'fixed-assets/depreciation/summary', params),
  // --- Inventory (Laravel gateway section 'inventory' -> accounting-apis
  // inventory module). Items are the full-CRUD master; movement documents
  // (receipts/issues/adjustments/transfers/revaluations) are WRITE-ONLY -
  // the backend exposes create/approve/post/reverse but no list or detail
  // endpoints, so visibility comes from the reports below. Reversals take
  // {reason} (min 5 chars). Verified against accounting-apis
  // src/modules/inventory/controllers/inventory.controller.ts + dto/index.ts.
  // NOTE: getInventoryItems() above reads through the 'gl' section for
  // journal-line product autocomplete; the methods below read through the
  // 'inventory' section so stewards only need ledger/inventory permissions.
  getInvItems: (params = {}) => ledger.get('inventory', 'inventory/items', params),
  getInvItem: (id) => ledger.get('inventory', `inventory/items/${id}`),
  getInvItemByCode: (code) => ledger.get('inventory', `inventory/items/by-code/${code}`),
  createInvItem: (data) => ledger.post('inventory', 'inventory/items', data),
  updateInvItem: (id, data) => ledger.patch('inventory', `inventory/items/${id}`, data),
  activateInvItem: (id) => ledger.post('inventory', `inventory/items/${id}/activate`),
  deactivateInvItem: (id) => ledger.post('inventory', `inventory/items/${id}/deactivate`),
  deleteInvItem: (id) => ledger.remove('inventory', `inventory/items/${id}`),
  createInvReceipt: (data) => ledger.post('inventory', 'inventory/receipts', data),
  approveInvReceipt: (id) => ledger.post('inventory', `inventory/receipts/${id}/approve`),
  postInvReceipt: (id) => ledger.post('inventory', `inventory/receipts/${id}/post`),
  reverseInvReceipt: (id, data) => ledger.post('inventory', `inventory/receipts/${id}/reverse`, data),
  linkApInvoice: (id, data) => ledger.post('inventory', `inventory/receipts/${id}/link-ap-invoice`, data),
  createInvIssue: (data) => ledger.post('inventory', 'inventory/issues', data),
  approveInvIssue: (id) => ledger.post('inventory', `inventory/issues/${id}/approve`),
  postInvIssue: (id) => ledger.post('inventory', `inventory/issues/${id}/post`),
  reverseInvIssue: (id, data) => ledger.post('inventory', `inventory/issues/${id}/reverse`, data),
  createInvAdjustment: (data) => ledger.post('inventory', 'inventory/adjustments', data),
  approveInvAdjustment: (id) => ledger.post('inventory', `inventory/adjustments/${id}/approve`),
  postInvAdjustment: (id) => ledger.post('inventory', `inventory/adjustments/${id}/post`),
  reverseInvAdjustment: (id, data) => ledger.post('inventory', `inventory/adjustments/${id}/reverse`, data),
  createInvTransfer: (data) => ledger.post('inventory', 'inventory/transfers', data),
  approveInvTransfer: (id) => ledger.post('inventory', `inventory/transfers/${id}/approve`),
  postInvTransfer: (id) => ledger.post('inventory', `inventory/transfers/${id}/post`),
  reverseInvTransfer: (id, data) => ledger.post('inventory', `inventory/transfers/${id}/reverse`, data),
  createInvRevaluation: (data) => ledger.post('inventory', 'inventory/revaluations', data),
  approveInvRevaluation: (id) => ledger.post('inventory', `inventory/revaluations/${id}/approve`),
  postInvRevaluation: (id) => ledger.post('inventory', `inventory/revaluations/${id}/post`),
  reverseInvRevaluation: (id, data) => ledger.post('inventory', `inventory/revaluations/${id}/reverse`, data),
  // Reports: valuation (optional category filter) returns {rows, totals};
  // movement and sales-margin REQUIRE from_date/to_date (+ optional item_id)
  // and return per-item received/issued/adjusted aggregates with opening and
  // closing balances; grni-exposure lists posted receipts with no AP invoice.
  getInvValuation: (params = {}) => ledger.get('inventory', 'inventory/reports/valuation', params),
  getInvMovement: (params = {}) => ledger.get('inventory', 'inventory/reports/movement', params),
  getInvGrniExposure: (params = {}) => ledger.get('inventory', 'inventory/reports/grni-exposure', params),
  getInvSalesMargin: (params = {}) => ledger.get('inventory', 'inventory/reports/sales-margin', params),
  // --- Tax & Statutory (Laravel gateway section 'tax' -> accounting-apis
  // tax-returns + withholding-tax modules). Returns flow DRAFT ->
  // IN_REVIEW -> APPROVED -> SUBMITTED -> ACCEPTED, then post; reject/
  // cancel/amend loop back with reasons (min 10 chars). The approve-family
  // (submit/accept/approve/unapprove/reject) runs as checker - 'submit',
  // 'accept', 'issue', 'verify' and 'confirm' are section-scoped checker
  // suffixes (see config/ledger.php). Verified against
  // src/modules/tax/tax-returns + withholding-tax controllers/dto.
  getTaxReturns: (params = {}) => ledger.get('tax', 'tax-returns', params),
  getTaxReturn: (id) => ledger.get('tax', `tax-returns/${id}`),
  createTaxReturn: (data) => ledger.post('tax', 'tax-returns', data),
  updateTaxReturn: (id, data) => ledger.patch('tax', `tax-returns/${id}`, data),
  deleteTaxReturn: (id) => ledger.remove('tax', `tax-returns/${id}`),
  submitTaxReturnForReview: (id) => ledger.patch('tax', `tax-returns/${id}/submit-for-review`),
  approveTaxReturn: (id, data = {}) => ledger.patch('tax', `tax-returns/${id}/approve`, data),
  unapproveTaxReturn: (id) => ledger.patch('tax', `tax-returns/${id}/unapprove`),
  rejectTaxReturn: (id, data) => ledger.patch('tax', `tax-returns/${id}/reject`, data),
  submitTaxReturn: (id, data) => ledger.patch('tax', `tax-returns/${id}/submit`, data),
  acceptTaxReturn: (id) => ledger.patch('tax', `tax-returns/${id}/accept`),
  cancelTaxReturn: (id, data) => ledger.patch('tax', `tax-returns/${id}/cancel`, data),
  postTaxReturn: (id, data = {}) => ledger.patch('tax', `tax-returns/${id}/post`, data),
  recordTaxPayment: (id, data) => ledger.post('tax', `tax-returns/${id}/record-payment`, data),
  amendTaxReturn: (id, data) => ledger.post('tax', `tax-returns/${id}/amend`, data),
  refreshTaxTotals: (id) => ledger.patch('tax', `tax-returns/${id}/refresh-totals`),
  markTaxReminderSent: (id) => ledger.patch('tax', `tax-returns/${id}/mark-reminder-sent`),
  getTaxReturnLines: (id, params = {}) => ledger.get('tax', `tax-returns/${id}/lines`, params),
  getTaxSuggestedLines: (id) => ledger.get('tax', `tax-returns/${id}/lines/suggested`),
  suggestTaxLines: (params = {}) => ledger.get('tax', 'tax-returns/suggest-lines', params),
  createTaxReturnLine: (id, data) => ledger.post('tax', `tax-returns/${id}/lines`, data),
  bulkCreateTaxReturnLines: (id, data) => ledger.post('tax', `tax-returns/${id}/lines/bulk`, data),
  replaceTaxReturnLines: (id, data) => ledger.post('tax', `tax-returns/${id}/lines/replace`, data),
  updateTaxReturnLine: (returnId, lineId, data) => ledger.patch('tax', `tax-returns/${returnId}/lines/${lineId}`, data),
  deleteTaxReturnLine: (returnId, lineId) => ledger.remove('tax', `tax-returns/${returnId}/lines/${lineId}`),
  getOverdueTaxReturns: (params = {}) => ledger.get('tax', 'tax-returns/overdue', params),
  getTaxReturnStats: (params = {}) => ledger.get('tax', 'tax-returns/stats', params),
  // Tax reports: VAT/WHT/FX over a date range (from_date/to_date required).
  getVatReport: (params = {}) => ledger.get('tax', 'tax-reports/vat', params),
  getWhtReport: (params = {}) => ledger.get('tax', 'tax-reports/wht', params),
  getFxReport: (params = {}) => ledger.get('tax', 'tax-reports/fx', params),
  // WHT certificates: PENDING -> ISSUED -> VERIFIED (or CANCELLED); can be
  // raised straight from a supplier invoice.
  getWhtCertificates: (params = {}) => ledger.get('tax', 'wht/certificates', params),
  getWhtCertificate: (id) => ledger.get('tax', `wht/certificates/${id}`),
  createWhtCertificate: (data) => ledger.post('tax', 'wht/certificates', data),
  createWhtCertificateFromInvoice: (invoiceId, data) => ledger.post('tax', `wht/certificates/from-supplier-invoice/${invoiceId}`, data),
  updateWhtCertificate: (id, data) => ledger.patch('tax', `wht/certificates/${id}`, data),
  issueWhtCertificate: (id) => ledger.patch('tax', `wht/certificates/${id}/issue`),
  verifyWhtCertificate: (id) => ledger.patch('tax', `wht/certificates/${id}/verify`),
  cancelWhtCertificate: (id) => ledger.patch('tax', `wht/certificates/${id}/cancel`),
  // WHT remittances: PENDING -> SUBMITTED -> CONFIRMED -> PAID (mark-paid),
  // with post settling from a bank/cash account and link-certificates
  // bundling certificates into the remittance.
  getWhtRemittances: (params = {}) => ledger.get('tax', 'wht/remittances', params),
  getWhtRemittance: (id) => ledger.get('tax', `wht/remittances/${id}`),
  createWhtRemittance: (data) => ledger.post('tax', 'wht/remittances', data),
  updateWhtRemittance: (id, data) => ledger.patch('tax', `wht/remittances/${id}`, data),
  submitWhtRemittance: (id, data = {}) => ledger.patch('tax', `wht/remittances/${id}/submit`, data),
  confirmWhtRemittance: (id) => ledger.patch('tax', `wht/remittances/${id}/confirm`),
  markWhtRemittancePaid: (id) => ledger.patch('tax', `wht/remittances/${id}/mark-paid`),
  postWhtRemittance: (id, data) => ledger.post('tax', `wht/remittances/${id}/post`, data),
  linkWhtCertificates: (id, data) => ledger.post('tax', `wht/remittances/${id}/link-certificates`, data),
  getWhtSummary: (params = {}) => ledger.get('tax', 'wht/summary', params),
  // --- Bank Reconciliation (Laravel gateway section 'recon' ->
  // accounting-apis bank-reconciliation module). One reconciliation per
  // (account x statement date): items are statement/book lines, clear +
  // unclear flip them in bulk, reconcile closes when the difference is
  // explained. clear/unclear/reconcile need the approve permission, so they
  // are section-scoped checker suffixes (see config/ledger.php).
  getBankReconciliations: (params = {}) => ledger.get('recon', 'bank-reconciliations', params),
  getBankReconciliation: (id) => ledger.get('recon', `bank-reconciliations/${id}`),
  createBankReconciliation: (data) => ledger.post('recon', 'bank-reconciliations', data),
  updateBankReconciliation: (id, data) => ledger.patch('recon', `bank-reconciliations/${id}`, data),
  deleteBankReconciliation: (id) => ledger.remove('recon', `bank-reconciliations/${id}`),
  reconcileBankAccount: (id, data = {}) => ledger.patch('recon', `bank-reconciliations/${id}/reconcile`, data),
  addReconItem: (id, data) => ledger.post('recon', `bank-reconciliations/${id}/items`, data),
  clearReconItems: (id, data) => ledger.patch('recon', `bank-reconciliations/${id}/items/clear`, data),
  unclearReconItems: (id, data) => ledger.patch('recon', `bank-reconciliations/${id}/items/unclear`, data),
  deleteReconItem: (id, itemId) => ledger.remove('recon', `bank-reconciliations/${id}/items/${itemId}`),
  // --- Fiscal / close (mirrors accounting-apis fiscal-years module via
  // Laravel gateway section 'close' -> /api/v1/fiscal/*) ---
  // Years: POST auto-generates monthly periods + seeds balances; close posts
  // CLOSING entries + carries balance-sheet forward; reverse-close voids them.
  getFiscalYears: (params = {}) => ledger.get('close', 'fiscal/years', params),
  getFiscalYear: (id) => ledger.get('close', `fiscal/years/${id}`),
  getCurrentFiscalYear: () => ledger.get('close', 'fiscal/years/current'),
  createFiscalYear: (data) => ledger.post('close', 'fiscal/years', data),
  updateFiscalYear: (id, data) => ledger.patch('close', `fiscal/years/${id}`, data),
  deleteFiscalYear: (id) => ledger.remove('close', `fiscal/years/${id}`),
  closeFiscalYear: (id) => ledger.patch('close', `fiscal/years/${id}/close`),
  reverseCloseFiscalYear: (id) => ledger.patch('close', `fiscal/years/${id}/reverse-close`),
  reopenFiscalYear: (id) => ledger.patch('close', `fiscal/years/${id}/reopen`),
  // Periods: only adjustment periods are manually creatable; regular monthly
  // periods come from year creation. Only OPEN periods are editable/deletable.
  getFiscalPeriods: (params = {}) => ledger.get('close', 'fiscal/periods', params),
  getFiscalPeriod: (id) => ledger.get('close', `fiscal/periods/${id}`),
  getCurrentFiscalPeriod: () => ledger.get('close', 'fiscal/periods/current'),
  getOpenFiscalPeriods: (params = {}) => ledger.get('close', 'fiscal/periods/open', params),
  createFiscalPeriod: (data) => ledger.post('close', 'fiscal/periods', data),
  updateFiscalPeriod: (id, data) => ledger.patch('close', `fiscal/periods/${id}`, data),
  deleteFiscalPeriod: (id) => ledger.remove('close', `fiscal/periods/${id}`),
  closePeriod: (id) => ledger.patch('close', `fiscal/periods/${id}/close`),
  reopenPeriod: (id) => ledger.patch('close', `fiscal/periods/${id}/reopen`),
  lockPeriod: (id) => ledger.patch('close', `fiscal/periods/${id}/lock`),
  unlockPeriod: (id) => ledger.patch('close', `fiscal/periods/${id}/unlock`),
  getPeriodChecklist: (id) => ledger.get('close', `fiscal/periods/${id}/checklist`),
  updatePeriodChecklist: (id, data) => ledger.patch('close', `fiscal/periods/${id}/checklist`, data),
  // --- Settings ---
  getNumberingSequences: (params = {}) => ledger.get('settings', 'configuration/numbering-sequences', params),
  createNumberingSequence: (data) => ledger.post('settings', 'configuration/numbering-sequences', data),
  updateNumberingSequence: (id, data) => ledger.patch('settings', `configuration/numbering-sequences/${id}`, data),
  deleteNumberingSequence: (id) => ledger.remove('settings', `configuration/numbering-sequences/${id}`),
  getPaymentMethods: (params = {}) => ledger.get('settings', 'payment-methods', params),
  createPaymentMethod: (data) => ledger.post('settings', 'payment-methods', data),
  updatePaymentMethod: (id, data) => ledger.patch('settings', `payment-methods/${id}`, data),
  deletePaymentMethod: (id) => ledger.remove('settings', `payment-methods/${id}`),
  getPostingRules: (params = {}) => ledger.get('settings', 'configuration/posting-rules', params),
  createPostingRule: (data) => ledger.post('settings', 'configuration/posting-rules', data),
  updatePostingRule: (id, data) => ledger.patch('settings', `configuration/posting-rules/${id}`, data),
  deletePostingRule: (id) => ledger.remove('settings', `configuration/posting-rules/${id}`),
  getJournalTemplates: (params = {}) => ledger.get('settings', 'configuration/journal-templates', params),
  createJournalTemplate: (data) => ledger.post('settings', 'configuration/journal-templates', data),
  updateJournalTemplate: (id, data) => ledger.patch('settings', `configuration/journal-templates/${id}`, data),
  deleteJournalTemplate: (id) => ledger.remove('settings', `configuration/journal-templates/${id}`),
  getCurrencies: (params = {}) => ledger.get('settings', 'configuration/currencies', params),
  createCurrency: (data) => ledger.post('settings', 'configuration/currencies', data),
  updateCurrency: (id, data) => ledger.patch('settings', `configuration/currencies/${id}`, data),
  deleteCurrency: (id) => ledger.remove('settings', `configuration/currencies/${id}`),
  getExchangeRates: (params = {}) => ledger.get('settings', 'configuration/exchange-rates', params),
  createExchangeRate: (data) => ledger.post('settings', 'configuration/exchange-rates', data),
  updateExchangeRate: (id, data) => ledger.patch('settings', `configuration/exchange-rates/${id}`, data),
  deleteExchangeRate: (id) => ledger.remove('settings', `configuration/exchange-rates/${id}`),
  getTaxTypes: (params = {}) => ledger.get('settings', 'configuration/tax-types', params),
  createTaxType: (data) => ledger.post('settings', 'configuration/tax-types', data),
  updateTaxType: (id, data) => ledger.patch('settings', `configuration/tax-types/${id}`, data),
  deleteTaxType: (id) => ledger.remove('settings', `configuration/tax-types/${id}`),
  getTaxRates: (params = {}) => ledger.get('settings', 'configuration/tax-rates', params),
  createTaxRate: (data) => ledger.post('settings', 'configuration/tax-rates', data),
  updateTaxRate: (id, data) => ledger.patch('settings', `configuration/tax-rates/${id}`, data),
  deleteTaxRate: (id) => ledger.remove('settings', `configuration/tax-rates/${id}`),
  getTaxCodes: (params = {}) => ledger.get('settings', 'configuration/tax-codes', params),
  createTaxCode: (data) => ledger.post('settings', 'configuration/tax-codes', data),
  updateTaxCode: (id, data) => ledger.patch('settings', `configuration/tax-codes/${id}`, data),
  deleteTaxCode: (id) => ledger.remove('settings', `configuration/tax-codes/${id}`),
  getTaxExemptions: (params = {}) => ledger.get('settings', 'configuration/tax-exemptions', params),
  createTaxExemption: (data) => ledger.post('settings', 'configuration/tax-exemptions', data),
  updateTaxExemption: (id, data) => ledger.patch('settings', `configuration/tax-exemptions/${id}`, data),
  deleteTaxExemption: (id) => ledger.remove('settings', `configuration/tax-exemptions/${id}`),
  getBranches: (params = {}) => ledger.get('settings', 'configuration/branches', params),
  createBranch: (data) => ledger.post('settings', 'configuration/branches', data),
  updateBranch: (id, data) => ledger.patch('settings', `configuration/branches/${id}`, data),
  deleteBranch: (id) => ledger.remove('settings', `configuration/branches/${id}`),
  getDepartments: (params = {}) => ledger.get('settings', 'configuration/departments', params),
  createDepartment: (data) => ledger.post('settings', 'configuration/departments', data),
  updateDepartment: (id, data) => ledger.patch('settings', `configuration/departments/${id}`, data),
  deleteDepartment: (id) => ledger.remove('settings', `configuration/departments/${id}`),
  getLocations: (params = {}) => ledger.get('settings', 'configuration/locations', params),
  createLocation: (data) => ledger.post('settings', 'configuration/locations', data),
  updateLocation: (id, data) => ledger.patch('settings', `configuration/locations/${id}`, data),
  deleteLocation: (id) => ledger.remove('settings', `configuration/locations/${id}`),
  getCostCentres: (params = {}) => ledger.get('settings', 'configuration/cost-centres', params),
  createCostCentre: (data) => ledger.post('settings', 'configuration/cost-centres', data),
  updateCostCentre: (id, data) => ledger.patch('settings', `configuration/cost-centres/${id}`, data),
  deleteCostCentre: (id) => ledger.remove('settings', `configuration/cost-centres/${id}`),
  getDimensions: (params = {}) => ledger.get('settings', 'configuration/dimensions', params),
  createDimension: (data) => ledger.post('settings', 'configuration/dimensions', data),
  updateDimension: (id, data) => ledger.patch('settings', `configuration/dimensions/${id}`, data),
  deleteDimension: (id) => ledger.remove('settings', `configuration/dimensions/${id}`),
  getDimensionValues: (params = {}) => ledger.get('settings', 'configuration/dimension-values', params),
  createDimensionValue: (data) => ledger.post('settings', 'configuration/dimension-values', data),
  updateDimensionValue: (id, data) => ledger.patch('settings', `configuration/dimension-values/${id}`, data),
  deleteDimensionValue: (id) => ledger.remove('settings', `configuration/dimension-values/${id}`),
  getAccountingPolicies: (params = {}) => ledger.get('settings', 'configuration/policies', params),
  createAccountingPolicy: (data) => ledger.post('settings', 'configuration/policies', data),
  updateAccountingPolicy: (id, data) => ledger.patch('settings', `configuration/policies/${id}`, data),
  deleteAccountingPolicy: (id) => ledger.remove('settings', `configuration/policies/${id}`),
  getFiscalPolicy: () => ledger.get('close', 'fiscal/policy'),
  updateFiscalPolicy: (data) => ledger.patch('close', 'fiscal/policy', data),
  // --- Reports ---
  getBalanceSheet: (params = {}) => ledger.get('reports', 'reports/balance-sheet', params),
  getIncomeStatement: (params = {}) => ledger.get('reports', 'reports/income-statement', params),
  getReportTrialBalance: (params = {}) => ledger.get('reports', 'reports/trial-balance', params),
  getGeneralLedgerReport: (params = {}) => ledger.get('reports', 'reports/general-ledger', params),
  getCashFlow: (params = {}) => ledger.get('reports', 'reports/cash-flow', params),
  getChangesInEquity: (params = {}) => ledger.get('reports', 'reports/changes-in-equity', params),
  // Exports stream the raw file (see ledger.download) - format is 'csv'|'xlsx'.
  exportTrialBalance: (params = {}) => ledger.download('reports', 'reports/trial-balance/export', params),
  exportBalanceSheet: (params = {}) => ledger.download('reports', 'reports/balance-sheet/export', params),
  exportIncomeStatement: (params = {}) => ledger.download('reports', 'reports/income-statement/export', params),
  exportGeneralLedger: (params = {}) => ledger.download('reports', 'reports/general-ledger/export', params),
};

// accounting (local cashbook - superseded by ledger/* but kept for history)
export const accountingAPI = {
  getDashboard: () => api.get('/accounting/dashboard'),
  getFeeReconciliation: (params) => api.get('/accounting/fee-reconciliation', { params }),
  getFeeReconciliationSource: (type, sourceRef, params = {}) =>
    api.get(`/accounting/fee-reconciliation/source/${type}/${encodeURIComponent(sourceRef)}`, { params }),
  recordFeeExceptionResolution: (data) => api.post('/accounting/fee-reconciliation/resolutions', data),
  // Resolved fee -> ledger bridge defaults for this school (read-only):
  // default bank/revenue ledger accounts + local -> ledger account map.
  getBridge: () => api.get('/accounting/bridge'),
  getSyncQueue: (params = {}) => api.get('/accounting/sync-queue', { params }),
  previewSyncQueueRetry: () => api.get('/accounting/sync-queue/retry-preview'),
  retrySyncQueue: () => api.post('/accounting/sync-queue/retry'),
  getProjectManagers: (params = {}) => api.get('/accounting/project-managers', { params }),
  getProjectManager: (id) => api.get(`/accounting/project-managers/${id}`),
  getAccounts: (params = {}) => api.get('/accounting/accounts', { params }),
  createAccount: (data) => api.post('/accounting/accounts', data),
  getVoucherHeads: () => api.get('/accounting/voucher-heads'),
  createVoucherHead: (data) => api.post('/accounting/voucher-heads', data),
  getTransactions: (params = {}) => api.get('/accounting/transactions', { params }),
  createTransaction: (data) => api.post('/accounting/transactions', data),
};

export const assessmentAPI = {
  // Assessment Types
  getAssessmentTypes: (params = {}) => api.get('/report-card/assessments/assessment-types', { params }),
  createAssessmentType: (data) => api.post('/report-card/assessments/assessment-types', data),
  getActiveAssessmentTypes: () => api.get('/report-card/assessments/assessment-types/active'),
  getAssessmentType: (id) => api.get(`/report-card/assessments/assessment-types/${id}`),
  updateAssessmentType: (id, data) => api.put(`/report-card/assessments/assessment-types/${id}`, data),
  deleteAssessmentType: (id) => api.delete(`/report-card/assessments/assessment-types/${id}`),

  // Assessment Configurations
  getAssessmentConfigurations: (params = {}) => api.get('/report-card/assessments/assessment-configurations', { params }),
  createAssessmentConfiguration: (data) => api.post('/report-card/assessments/assessment-configurations', data),
  getAssessmentConfiguration: (id) => api.get(`/report-card/assessments/assessment-configurations/${id}`),
  updateAssessmentConfiguration: (id, data) => api.put(`/report-card/assessments/assessment-configurations/${id}`, data),
  bulkUpdateAssessmentConfigurations: (data) => api.put('/report-card/assessments/assessment-configurations/bulk', data),
  deleteAssessmentConfiguration: (id) => api.delete(`/report-card/assessments/assessment-configurations/${id}`),
  getConfigurationsForPaper: (paperId, params) => api.get(`/report-card/assessments/assessment-configurations/for-paper/${paperId}`, { params }),
  validateWeights: (data) => api.post('/report-card/assessments/assessment-configurations/validate-weights', data),

  // Student Marks
  getStudentMarks: (params = {}) => api.get('/report-card/assessments/student-marks', { params }),
  createStudentMark: (data) => api.post('/report-card/assessments/student-marks', data),
  bulkCreateStudentMarks: (data) => api.post('/report-card/assessments/student-marks/bulk', data),
  superBulkCreateStudentMarks: (data) => api.post('/report-card/assessments/student-marks/super-bulk', data),
  getMarksForAssessment: (assessmentConfigId) => api.get(`/report-card/assessments/student-marks/for-assessment/${assessmentConfigId}`),
  getMySubjectPapers: () => api.get('/report-card/assessments/student-marks/my-subject-papers'),
  getMyClasses: () => api.get('/report-card/assessments/student-marks/my-classes'),
  getMyClassTeacherClasses: () => api.get('/report-card/assessments/student-marks/my-class-teacher-classes'),
  getStudentSummary: (studentId, params) => api.get(`/report-card/assessments/student-marks/student-summary/${studentId}`, { params }),
  updateStudentMark: (id, data) => api.put(`/report-card/assessments/student-marks/${id}`, data),
  deleteStudentMark: (id) => api.delete(`/report-card/assessments/student-marks/${id}`),
  exportMarks: (assessmentConfigId) => api.get('/report-card/assessments/student-marks/export', {
    params: { assessment_configuration_id: assessmentConfigId },
    responseType: 'blob',
  }),
  downloadMarksTemplate: (assessmentConfigId) => api.get('/report-card/assessments/student-marks/template', {
    params: { assessment_configuration_id: assessmentConfigId },
    responseType: 'blob',
  }),
  importMarks: (assessmentConfigId, file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('assessment_configuration_id', assessmentConfigId);
    return api.post('/report-card/assessments/student-marks/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Assessment Reports
  getAssessmentProgress: (params) => api.get('/report-card/assessments/reports/assessment-progress', { params }),
  getTeacherWorkload: (teacherId, params) => api.get(`/report-card/assessments/reports/teacher-workload/${teacherId}`, { params }),
  getAllTeachersWorkload: (params) => api.get('/report-card/assessments/reports/teacher-workload', { params }),
  getAnalytics: (params) => api.get('/report-card/assessments/reports/analytics', { params }),
};

export const gradingAPI = {
  // Grading Scales
  getGradingScales: (params = {}) => api.get('/report-card/grading/scales', { params }),
  createGradingScale: (data) => api.post('/report-card/grading/scales', data),
  updateGradingScale: (id, data) => api.put(`/report-card/grading/scales/${id}`, data),
  deleteGradingScale: (id) => api.delete(`/report-card/grading/scales/${id}`),
  getActiveGradingScales: () => api.get('/report-card/grading/scales/active'),

  // Grade Boundaries
  getGradeBoundaries: (params = {}) => api.get('/report-card/grading/boundaries', { params }),
  createGradeBoundary: (data) => api.post('/report-card/grading/boundaries', data),
  updateGradeBoundary: (id, data) => api.put(`/report-card/grading/boundaries/${id}`, data),
  deleteGradeBoundary: (id) => api.delete(`/report-card/grading/boundaries/${id}`),
  getBoundariesForScale: (scaleId) => api.get(`/report-card/grading/boundaries/for-scale/${scaleId}`),

  // Class Grading Scales
  getClassGradingScales: (params = {}) => api.get('/report-card/grading/class-scales', { params }),
  assignGradingScale: (data) => api.post('/report-card/grading/class-scales', data),
  bulkAssignGradingScale: async (data) => {
    const classIds = Array.isArray(data?.class_ids) ? data.class_ids : [];
    const { class_ids, ...rest } = data || {};
    return Promise.all(classIds.map((classId) => api.post('/report-card/grading/class-scales', {
      ...rest,
      class_id: classId
    })));
  },
  updateClassGradingScale: (id, data) => api.put(`/report-card/grading/class-scales/${id}`, data),
  deleteClassGradingScale: (id) => api.delete(`/report-card/grading/class-scales/${id}`),
  getClassGradingScaleForClass: (classId, params = {}) => api.get(`/report-card/grading/class-scales/for-class/${classId}`, { params }),

  // Grade Calculation
  calculateGrade: (percentage, scaleId) => api.post(`/report-card/grading/scales/${scaleId}/get-grade`, { percentage }),
};

export const academicAPI = {
  // Classes
  getClasses: () => api.get('/academic/classes'),
  createClass: (data) => api.post('/academic/classes', data),
  updateClass: (id, data) => api.put(`/academic/classes/${id}`, data),
  deleteClass: (id) => api.delete(`/academic/classes/${id}`),
  getAllClasses: () => api.get('/academic/all-classes'),
  
  // Streams
  getStreams: () => api.get('/academic/streams'),
  createStream: (data) => api.post('/academic/streams', data),
  attachStream: (classId, data) => api.post(`/academic/classes/${classId}/streams`, data),
  detachStream: (classId, streamId) => api.delete(`/academic/classes/${classId}/streams/${streamId}`),
  
  // Class Streams
  getClassStreams: () => api.get('/academic/class-streams'),
  createClassStream: (data) => api.post('/academic/class-streams', data),
  getClassStreamsForFilter: () => api.get('/academic/class-streams-filter'),
  
  // Levels
  getLevels: () => api.get('/academic/levels'),
  createLevel: (data) => api.post('/academic/levels', data),
  updateLevel: (id, data) => api.put(`/academic/levels/${id}`, data),
  deleteLevel: (id) => api.delete(`/academic/levels/${id}`),

  // Terms
  getTerms: (params = {}) => api.get('/academic/terms', { params }),
  createTerm: (data) => api.post('/academic/terms', data),
  updateTerm: (id, data) => api.put(`/academic/terms/${id}`, data),
  deleteTerm: (id) => api.delete(`/academic/terms/${id}`),
  setCurrentTerm: (id) => api.patch(`/academic/terms/${id}/set-current`),

};

export const communicationAPI = {
  // Email
  getEmailLogs: (params = {}) => api.get('/communications/email-logs', { params }),
  sendBulkReminders: (data) => api.post('/communications/bulk-reminders', data),
  
  // SMS
  getSmsLogs: (params = {}) => api.get('/communications/sms-logs', { params }),
  getSmsStatistics: (params = {}) => api.get('/communications/sms-statistics', { params }),
  sendSms: (data) => api.post('/communications/send-sms', data),
  sendSmsToParent: (data) => api.post('/communications/send-sms-to-parent', data),
  
  // Student filtering
  getFilteredStudents: (params = {}) => api.get('/communications/filtered-students', { params }),
};

// RBAC API
export const rbacAPI = {
  getRoles: () => api.get('/rbac/roles'),
  getPermissions: () => api.get('/rbac/permissions'),
  getResources: () => api.get('/rbac/resources'),
  getUserPermissions: (userId) => api.get(`/rbac/user-permissions/${userId}`),
  assignPermissions: (data) => api.post('/rbac/user-permissions/assign', data),
  removePermission: (data) => api.delete('/rbac/user-permissions/remove', { data }),
};

// User API
export const userAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.post(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  bulkAction: (data) => api.post('/users/bulk-action', data),
  resetPassword: (id, data) => api.patch(`/users/${id}/reset-password`, data),
  toggleStatus: (id) => api.patch(`/users/${id}/toggle-status`),
  getRoles: () => api.get('/users/roles'),
  uploadProfilePicture: (id, formData) => api.post(`/users/${id}/profile-picture`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteProfilePicture: (id) => api.delete(`/users/${id}/profile-picture`),
};

// Role API
export const roleAPI = {
  getAll: (params = {}) => api.get('/roles', { params }),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  getPermissions: (id) => api.get(`/roles/${id}/permissions`),
  assignPermissions: (id, data) => api.post(`/roles/${id}/permissions`, data),
  toggleStatus: (id) => api.patch(`/roles/${id}/toggle-status`),
};

// School API
export const schoolAPI = {
  get: () => api.get('/school'),
  create: (data) => api.post('/school', data),
  update: (data) => api.put('/school', data),
  uploadLogo: (formData) => api.post('/school/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: () => api.delete('/school/logo'),
};

// Clearance API
export const clearanceAPI = {
  getSettings: () => api.get('/clearance/settings'),
  createSetting: (data) => api.post('/clearance/settings', data),
  updateSetting: (id, data) => api.put(`/clearance/settings/${id}`, data),
  deleteSetting: (id) => api.delete(`/clearance/settings/${id}`),
  checkEligibility: (data) => api.post('/clearance/check-eligibility', data),
  generateCards: (data) => api.post('/clearance/generate-cards', data),
  getClearedStudents: (params = {}) => api.get('/clearance/cleared-students', { params }),
  downloadCards: (data) => api.post('/clearance/download-cards', data),
};

// Teacher API
export const teacherAPI = {
  getAll: (params = {}) => api.get('/report-card/teachers', { params }),
  getById: (id) => api.get(`/report-card/teachers/${id}`),
  create: (data) => api.post('/report-card/teachers', data),
  update: (id, data) => api.put(`/report-card/teachers/${id}`, data),
  delete: (id) => api.delete(`/report-card/teachers/${id}`),
  getActive: () => api.get('/report-card/teachers/active'),
  getSchedule: (id) => api.get(`/report-card/teachers/${id}/schedule`),
};

export const reportCardAPI = {
  getSubjectPapers: (params = {}) => api.get('/report-card/subject-papers', { params }),
  storeSubjectPaper: (data) => api.post('/report-card/subject-papers', data),
  getSubjectPapersForClass: (params = {}) => api.get('/report-card/subject-papers/for-class', { params }),
  updateSubjectPaper: (id, data) => api.put(`/report-card/subject-papers/${id}`, data),
  deleteSubjectPaper: (id) => api.delete(`/report-card/subject-papers/${id}`),
  getSubjects: (params = {}) => api.get('/report-card/subjects', { params }),
  storeSubject: (data) => api.post('/report-card/subjects', data),
  getActiveSubjects: () => api.get('/report-card/subjects/active'),
  showSubject: (id) => api.get(`/report-card/subjects/${id}`),
  updateSubject: (id, data) => api.put(`/report-card/subjects/${id}`, data),
  deleteSubject: (id) => api.delete(`/report-card/subjects/${id}`),
  getClassStreamPapers: (params = {}) => api.get('/report-card/class-stream-papers', { params }),
  storeClassStreamPaper: (data) => api.post('/report-card/class-stream-papers', data),
  bulkAssignClassStreamPapers: (papers) => api.post('/report-card/class-stream-papers', { papers }),
  updateClassStreamPaper: (id, data) => api.put(`/report-card/class-stream-papers/${id}`, data),
  deleteClassStreamPaper: (id) => api.delete(`/report-card/class-stream-papers/${id}`),

  // Conduct categories and ratings
  getConductCategories: () => api.get('/report-card/conduct-categories'),
  createConductCategory: (data) => api.post('/report-card/conduct-categories', data),
  updateConductCategory: (id, data) => api.put(`/report-card/conduct-categories/${id}`, data),
  deleteConductCategory: (id) => api.delete(`/report-card/conduct-categories/${id}`),
  getConductRatings: () => api.get('/report-card/conduct-ratings'),
  createConductRating: (data) => api.post('/report-card/conduct-ratings', data),
  updateConductRating: (id, data) => api.put(`/report-card/conduct-ratings/${id}`, data),
  deleteConductRating: (id) => api.delete(`/report-card/conduct-ratings/${id}`),

  // Student conduct
  getStudentConduct: (params = {}) => api.get('/report-card/conduct', { params }),
  createStudentConduct: (data) => api.post('/report-card/conduct', data),
  bulkCreateStudentConduct: (data) => api.post('/report-card/conduct/bulk', data),
  bulkCreateStudentConduct: (data) => api.post('/report-card/conduct/bulk', data),
  getStudentConductForStudent: (studentId, params = {}) => api.get(`/report-card/conduct/student/${studentId}`, { params }),
  updateStudentConduct: (id, data) => api.put(`/report-card/conduct/${id}`, data),
  deleteStudentConduct: (id) => api.delete(`/report-card/conduct/${id}`),

  // Subject comments
  getSubjectComments: (params = {}) => api.get('/report-card/subject-comments', { params }),
  createSubjectComment: (data) => api.post('/report-card/subject-comments', data),
  getSubjectCommentsForStudent: (studentId, params = {}) => api.get(`/report-card/subject-comments/student/${studentId}`, { params }),
  updateSubjectComment: (id, data) => api.put(`/report-card/subject-comments/${id}`, data),
  deleteSubjectComment: (id) => api.delete(`/report-card/subject-comments/${id}`),

  // General comments
  getGeneralComments: (params = {}) => api.get('/report-card/general-comments', { params }),
  createGeneralComment: (data) => api.post('/report-card/general-comments', data),
  getGeneralCommentForStudent: (studentId, params = {}) => api.get(`/report-card/general-comments/student/${studentId}`, { params }),
  updateGeneralComment: (id, data) => api.put(`/report-card/general-comments/${id}`, data),
  deleteGeneralComment: (id) => api.delete(`/report-card/general-comments/${id}`),

  // Report cards
  getReportCards: (params = {}) => api.get('/report-card/report-cards', { params }),
  getReportCard: (id) => api.get(`/report-card/report-cards/${id}`),
  getReportCardForStudent: (studentId, params = {}) => api.get(`/report-card/report-cards/student/${studentId}`, { params }),
  updateReportCard: (id, data) => api.put(`/report-card/report-cards/${id}`, data),
  bulkUpdateReportCards: (data) => api.put('/report-card/report-cards-bulk', data),
  deleteReportCard: (id) => api.delete(`/report-card/report-cards/${id}`),
  publishReportCard: (id) => api.post(`/report-card/report-cards/${id}/publish`),
  bulkPublishReportCards: (data) => api.post('/report-card/report-cards-bulk-publish', data),

  // Report card templates
  getReportCardTemplates: (params = {}) => api.get('/report-card/templates', { params }),
  getReportCardTemplate: (id) => api.get(`/report-card/templates/${id}`),
  createReportCardTemplate: (data) => api.post('/report-card/templates', data),
  updateReportCardTemplate: (id, data) => api.put(`/report-card/templates/${id}`, data),
  deleteReportCardTemplate: (id) => api.delete(`/report-card/templates/${id}`),

  // Report generation
  generateReportForStudent: (data) => api.post('/report-card/generate-report/student', data),
  generateReportForClass: (data) => api.post('/report-card/generate-report/class', data),

  // Comment templates
  getCommentTemplates: (params = {}) => api.get('/report-card/comment-templates', { params }),
  createCommentTemplate: (data) => api.post('/report-card/comment-templates', data),
  updateCommentTemplate: (id, data) => api.put(`/report-card/comment-templates/${id}`, data),
  bulkCreateCommentTemplates: (data) => api.post('/report-card/comment-templates/bulk', data),
  bulkDeleteCommentTemplates: (ids) => api.delete('/report-card/comment-templates/bulk', { data: { ids } }),
  deleteCommentTemplate: (id) => api.delete(`/report-card/comment-templates/${id}`),

  // Comment context (grade boundaries derived from marks + conduct)
  getCommentContext: (studentId, params) => api.get(`/report-card/comment-context/${studentId}`, { params }),

  // Comment generation (P5 - Comment Automation V2)
  suggestCommentTemplates: (params) => api.get('/report-card/comment-templates/suggest', { params }),
  generateComments: (data) => api.post('/report-card/comments/generate', data),

  // Auto-generate subject comment from template based on mark percentage
  // Uses upsert: fetches existing comment for student+subject+term, updates or creates
  autoGenerateSubjectComment: (data) => {
    const { student_id, subject_id, term_id, academic_year, comment_text } = data;
    return api.post('/report-card/subject-comments', { student_id, subject_id, term_id, academic_year, comment_text });
  },

  autoGenerateGeneralComment: (data) => {
    const { student_id, term_id, academic_year, class_teacher_comment } = data;
    return api.post('/report-card/general-comments', { student_id, term_id, academic_year, class_teacher_comment });
  },

  bulkGenerateClassTeacherComments: (data) => api.post('/report-card/general-comments/bulk-generate', data),
};

export default api;

// SchoolPay Integration API
export const schoolPayAPI = {
  // Transaction Sync
  syncByDate: (date) => api.post('/schoolpay/sync/date', { date }),
  syncByRange: (fromDate, toDate) => api.post('/schoolpay/sync/range', { from_date: fromDate, to_date: toDate }),

  // Transaction Management
  getTransactions: (params = {}) => api.get('/schoolpay/transactions', { params }),
  getTransactionStats: () => api.get('/schoolpay/transactions/stats'),
  manualMatch: (data) => api.post('/schoolpay/transactions/match', data),

  // Adhoc Payments
  adhocRegister: (data) => api.post('/schoolpay/adhoc/register', data),
  adhocRequest: (data) => api.post('/schoolpay/adhoc/request', data),
  adhocCheck: (reference) => api.get(`/schoolpay/adhoc/check/${reference}`),
  getAdhocPayments: (params = {}) => api.get('/schoolpay/adhoc/payments', { params }),
  collectForStudent: (studentId, data) => api.post(`/schoolpay/adhoc/collect/${studentId}`, data),

  // Logs
  getLogs: (params = {}) => api.get('/schoolpay/logs', { params }),
};

export const studentPortalAPI = {
  login: (data, config = {}) => api.post('/student-portal/login', data, config),
  logout: (token) => api.post('/student-portal/logout', {}, { headers: { Authorization: `Bearer ${token}` } }),
  changePassword: (data, token) => api.put('/student-portal/change-password', data, { headers: { Authorization: `Bearer ${token}` } }),
  getProfile: (token) => api.get('/student-portal/profile', { headers: { Authorization: `Bearer ${token}` } }),
  getReportCards: (token) => api.get('/student-portal/report-cards', { headers: { Authorization: `Bearer ${token}` } }),
  getReportCard: (id, token) => api.get(`/student-portal/report-cards/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  getFeeSummary: (token) => api.get('/student-portal/fees', { headers: { Authorization: `Bearer ${token}` } }),
  payFees: (data, token) => api.post('/student-portal/pay-fees', data, { headers: { Authorization: `Bearer ${token}` } }),
  checkFeePaymentStatus: (reference, token) => api.get(`/student-portal/pay-fees/status/${reference}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Admin
  activatePortal: (studentId, data) => api.post(`/admin/student-portal/activate/${studentId}`, data),
  deactivatePortal: (studentId) => api.post(`/admin/student-portal/deactivate/${studentId}`),
  resetPortalPassword: (studentId, data) => api.post(`/admin/student-portal/reset-password/${studentId}`, data),
  bulkActivatePortal: (data) => api.post('/admin/student-portal/bulk-activate', data),
};
